import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useReloadOnRouteEnter } from "../hooks/useReloadOnRouteEnter";
import { useTheme } from "../components/ThemeProvider";
import { useAuth } from "../context/AuthContext";
import usePermiso from "../hooks/usePermiso";
import { dialogs } from "../context/DialogContext";
import api from "../axiosConfig";
import AddressAutocompleteInput from "../components/AddressAutocompleteInput";
import ModalGuardiasSabados from "../components/agenda/ModalGuardiasSabados";
import ModalGuardiaDia from "../components/agenda/ModalGuardiaDia";
import FichaResumenPropiedad from "../components/propiedades/FichaResumenPropiedad";
import CitaLinkTelefono from "../components/cita/CitaLinkTelefono";
import CitaLinkReferencia from "../components/cita/CitaLinkReferencia";
import CitaSelectorHora from "../components/cita/CitaSelectorHora";
import BloqueCitaCalendario from "../components/agenda/BloqueCitaCalendario";
import NoDispFechaHoraPicker from "../components/agenda/NoDispFechaHoraPicker";
import { ndFormFromApi, ndFormToApi, formatNdPeriodo, ndTodoElDia, dedupeNdPorAgente, buildAgentesColumnasIndex, ndParcialesParaColumnas, formatNdMotivoCorto, ndFranjaSemanaPx, ndFranjaTodoElDiaSemanaPx } from "../utils/noDispFechaHora";
import ModalFeedbackVisita from "../components/ModalFeedbackVisita";
import ModalCita from "../components/ModalCita";
import {
  loadCitaModalFromAgenda,
  fetchPropiedadPorRef,
  buildCitaDateTimeISO,
  addMinutosHora,
  ensureVisitaRangoISO,
  splitPropiedadesCita,
  readAgendaTextoLibre,
  parseMetadataMovil,
} from "../utils/citaAgenda";
import { useAgendaCitaPointer } from "../hooks/useAgendaCitaPointer";
import { useCitaModalOverlayOptional } from "../context/CitaModalOverlayContext";
import {
  ALTO_HORA_AGENDA_PX,
  HORA_INICIO_AGENDA,
  fechaLocalDeIso,
  posicionBloqueCita,
  computeAgendaOverlapLayout,
} from "../utils/agendaCalendario";
import {
  getColoresTipoCitaAgenda,
  getColorAgenteAgenda,
  getColorUsuarioAgenda,
  getColorCitaVistaTodos,
  getEstiloCitaAgenda,
  getEstiloNdAgenda,
  mezclarColorAgenda,
  textoSobreColorHex,
  AGENDA_TEXTO_CITA,
  estilosCajaCita,
  propsLeyendaTipo,
} from "../constants/agendaColores";
import "../styles/agendaCitaTipo.css";
import { inicialesNombreApellido } from "../utils/helpers";
import {
  enriquecerCitaAgenda,
  linea1BloqueCita,
  linea2BloqueCita,
  nombreCaptacionCita,
  telefonoCaptacionCita,
} from "../utils/agendaCitaDisplay";

const TIPOS = ["visita", "llamada", "reunion", "captacion", "valoracion", "otro"];
const TIPOS_NO_DISP = ["vacaciones", "baja", "festivo", "otro"];

function etiquetaGuardiaSabadoSemana(guardia) {
  if (!guardia) return null;
  const nombres = [];
  if (guardia.staff_nombre) nombres.push(guardia.staff_nombre);
  if (Array.isArray(guardia.agentes_nombres)) nombres.push(...guardia.agentes_nombres);
  const iniciales = nombres.map((n) => inicialesNombreApellido(n)).filter(Boolean).join(" ");
  if (!iniciales) return null;
  return { iniciales, titulo: nombres.join(", ") };
}

/** Permisos granulares (permisosTree.js): id por cada tipo de cita / no-disponibilidad. */
const TIPO_CITA_PERM_ID = {
  visita: "ag_cita_visita",
  llamada: "ag_cita_llamada",
  reunion: "ag_cita_reunion",
  captacion: "ag_cita_captacion",
  valoracion: "ag_cita_valoracion",
  otro: "ag_cita_otro",
};
const TIPO_ND_PERM_ID = {
  vacaciones: "ag_nd_vacaciones",
  baja: "ag_nd_baja",
  festivo: "ag_nd_festivo",
  otro: "ag_nd_otro",
};
const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DIAS_SEMANA = ["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"];
const CITA_HORAS_RAPIDAS = [9, 10, 11, 12, 13, 16, 17, 18];
const CITA_MIN_RAPIDOS = [0, 10, 15, 30, 45];
const CITA_DURACIONES_RAPIDAS = [30, 45, 60, 90, 120];
/** Mismo rojo que el botón «Festivos» (cabecera y cuerpo del día en agenda). */
const COLOR_BOTON_FESTIVOS = "#dc2626";

function pad(n) { return String(n).padStart(2, "0"); }
function fechaISO(d) { return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }

function mezclarColor(hex, conHex, ratio) { return mezclarColorAgenda(hex, conHex, ratio); }
function horaStr(iso) { const d = new Date(iso); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; }

const HORA_FIN_AGENDA = 19;

function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Vista compacta dd/mm hh:mm para resumen en modal cita */
function formatCitaPreview(dt) {
  if (!dt || String(dt).length < 10) return "—";
  const s = String(dt);
  const d = s.slice(0, 10);
  const t = s.includes("T") ? s.slice(11, 16) : "";
  return `${d.slice(8)}/${d.slice(5, 7)} ${t || "—"}`;
}

function parsePgUuidArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean).map(String);
  if (typeof val === "string" && val.startsWith("{")) {
    const inner = val.slice(1, -1);
    if (!inner.trim()) return [];
    return inner.split(",").map((s) => s.replace(/^"|"$/g, "").trim()).filter(Boolean);
  }
  return [];
}

function nombreContactoCita(cita) {
  if (!cita) return "";
  const cap = nombreCaptacionCita(cita);
  if (cap) return cap;
  if (cita.cliente_id || cita.cliente_nombre) {
    return [cita.cliente_nombre, cita.cliente_apellido].filter(Boolean).join(" ").trim();
  }
  if (cita.contacto_id || cita.contacto_nombre) {
    return [cita.contacto_nombre, cita.contacto_apellidos].filter(Boolean).join(" ").trim();
  }
  if (cita.lead_id || cita.lead_nombre) {
    return [cita.lead_nombre, cita.lead_apellido].filter(Boolean).join(" ").trim();
  }
  const { clienteTexto } = readAgendaTextoLibre(cita);
  if (clienteTexto) return clienteTexto;
  if (cita.titulo) {
    const t = String(cita.titulo).replace(/^Visita\s*[—\-–]\s*/i, "").trim();
    if (t && !/^visita$/i.test(t)) return t;
  }
  return "";
}

function telefonoContactoCita(cita) {
  if (!cita) return "";
  return (
    (cita.telefono_contacto && String(cita.telefono_contacto).trim()) ||
    (cita.lead_telefono && String(cita.lead_telefono).trim()) ||
    (cita.cliente_telefono && String(cita.cliente_telefono).trim()) ||
    (cita.contacto_telefono && String(cita.contacto_telefono).trim()) ||
    ""
  );
}

function minutosDuracionCita(cita) {
  if (!cita?.fecha_inicio) return 60;
  const ini = new Date(cita.fecha_inicio);
  const fin = new Date(cita.fecha_fin || cita.fecha_inicio);
  const m = Math.round((fin.getTime() - ini.getTime()) / 60000);
  return m > 0 ? m : 60;
}

function finDesdeInicioYDuracion(inicioLocal, mins) {
  if (!inicioLocal) return "";
  const d = new Date(inicioLocal);
  if (isNaN(d.getTime())) return "";
  return toDatetimeLocal(new Date(d.getTime() + (Number(mins) || 60) * 60000).toISOString());
}

function mapCitaToForm(cita) {
  const { clienteTexto, propsTexto } = readAgendaTextoLibre(cita);
  return {
    ...cita,
    fecha_inicio: toDatetimeLocal(cita.fecha_inicio),
    fecha_fin: toDatetimeLocal(cita.fecha_fin),
    propiedad_ids: parsePgUuidArray(cita.propiedad_ids),
    telefono_contacto: telefonoContactoCita(cita),
    duracion_minutos: minutosDuracionCita(cita),
    cliente_texto: clienteTexto || null,
    propiedades_texto: propsTexto,
  };
}

export default function Agenda() {
  const { theme } = useTheme();
  const { usuario } = useAuth();
  const { puede, esPropio } = usePermiso();
  /** Sin filtro de usuario o alcance agenda=propio: siempre la agenda del agente logueado. */
  const forzarAgendaPropia = !puede("ag_filtro_usuario", "ver") || esPropio("agenda", "alcance_ver");
  const esRolAgente = String(usuario?.rol || "").toLowerCase() === "agente";
  const c = theme.colors;
  const isDark = theme.name === "dark";

  // ── Permisos granulares (permisosTree.js: nodo "agenda") ──
  const puedeVerNavHoy = puede("ag_nav_hoy", "ver");
  const puedeVerVistaDia = puede("ag_vista_dia", "ver");
  const puedeVerVistaSemana = puede("ag_vista_semana", "ver");
  const puedeVerVistaMes = puede("ag_vista_mes", "ver");
  const puedeVerFiltroUsuario = puede("ag_filtro_usuario", "ver");
  const puedeVerTipoCita = (t) => puede(TIPO_CITA_PERM_ID[t] || "ag_cita_otro", "ver");
  const puedeVerTipoNd = (t) => puede(TIPO_ND_PERM_ID[t] || "ag_nd_otro", "ver");
  const puedeModificarTipoNd = (t) => puede(TIPO_ND_PERM_ID[t] || "ag_nd_otro", "modificar");

  const puedeVerNuevaCita = puede("ag_nueva_cita", "ver");
  const puedeVerCampoTitulo = puede("ag_cita_campo_titulo", "ver");
  const puedeModificarCampoTitulo = puede("ag_cita_campo_titulo", "modificar");
  const puedeVerCampoEstado = puede("ag_cita_campo_estado", "ver");
  const puedeModificarCampoEstado = puede("ag_cita_campo_estado", "modificar");
  const puedeVerCampoUsuario = puede("ag_cita_campo_usuario", "ver");
  const puedeModificarCampoUsuario = puede("ag_cita_campo_usuario", "modificar");
  const puedeVerCampoContacto = puede("ag_cita_campo_contacto", "ver");
  const puedeModificarCampoContacto = puede("ag_cita_campo_contacto", "modificar");
  const puedeVerCampoPropiedades = puede("ag_cita_campo_propiedades", "ver");
  const puedeModificarCampoPropiedades = puede("ag_cita_campo_propiedades", "modificar");
  const puedeVerCampoDireccion = puede("ag_cita_campo_direccion", "ver");
  const puedeModificarCampoDireccion = puede("ag_cita_campo_direccion", "modificar");
  const puedeVerCampoNotas = puede("ag_cita_campo_notas", "ver");
  const puedeModificarCampoNotas = puede("ag_cita_campo_notas", "modificar");
  const puedeVerCampoFechaHora = puede("ag_cita_campo_fecha_hora", "ver");
  const puedeModificarCampoFechaHora = puede("ag_cita_campo_fecha_hora", "modificar");
  const puedeVerCancelarCita = puede("ag_cita_cancelar", "ver");
  const puedeVerEliminarCita = puede("ag_cita_eliminar", "ver");
  const puedeVerResultadoVisita = puede("ag_cita_resultado_visita", "ver");

  const puedeVerNoDisp = puede("ag_no_disp", "ver");
  const puedeModificarNoDisp = puede("ag_no_disp", "modificar");

  const puedeVerVacaciones = puede("ag_modal_vacaciones", "ver");
  const puedeModificarVacaciones = puede("ag_modal_vacaciones", "modificar");
  const puedeModificarVacAgentes = puede("ag_vac_agentes", "modificar");
  const puedeModificarVacFechas = puede("ag_vac_fechas", "modificar");
  const puedeModificarVacDescripcion = puede("ag_vac_descripcion", "modificar");
  const puedeVerVacEliminar = puede("ag_vac_eliminar", "ver");

  const puedeVerFestivos = puede("ag_festivos", "ver");
  const puedeModificarFestivos = puede("ag_festivos", "modificar");
  const puedeAltaFestivos = puede("ag_festivos", "alta");
  const puedeBajaFestivos = puede("ag_festivos", "baja");
  const puedeModificarFestFecha = puede("ag_fest_fecha", "modificar");
  const puedeModificarFestNombre = puede("ag_fest_nombre", "modificar");
  const puedeModificarFestAmbito = puede("ag_fest_ambito", "modificar");
  const puedeVerFestActivar = puede("ag_fest_activar", "ver");
  const puedeModificarFestActivar = puede("ag_fest_activar", "modificar");

  const puedeVerGuardias = puede("ag_guardias", "ver");
  const puedeVerGuardiaDia = puede("ag_guardia_dia", "ver");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const citaOverlay = useCitaModalOverlayOptional();

  const [vista, setVista] = useState("semana");
  const [fechaActual, setFechaActual] = useState(new Date());
  const [usuariosAgenda, setUsuariosAgenda] = useState([]);
  const [agentes, setAgentes] = useState([]);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState("todos");
  const usuarioInicializadoRef = useRef(false);
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [citas, setCitas] = useState([]);
  const [noDisponible, setNoDisponible] = useState([]);
  const [modalCita, setModalCita] = useState(null);
  const [modalFeedbackAgendaId, setModalFeedbackAgendaId] = useState(null);
  const [modalNoDisp, setModalNoDisp] = useState(false);
  const [modalEditNd, setModalEditNd] = useState(null);
  const [editNdForm, setEditNdForm] = useState({});
  const [citaForm, setCitaForm] = useState({});
  const [noDispForm, setNoDispForm] = useState({});
  const [loading, setLoading] = useState(false);
  const guardandoDragRef = useRef(false);
  const interactuandoCitaRef = useRef(false);
  const columnaDiaRef = useRef(null);

  // Festivos
  const [festivos, setFestivos] = useState([]);
  const [modalFestivos, setModalFestivos] = useState(false);
  const [festivosYear, setFestivosYear] = useState(new Date().getFullYear());
  const [editFestivo, setEditFestivo] = useState(null);
  const [festivoForm, setFestivoForm] = useState({ fecha: '', nombre: '', ambito: 'local' });
  const [savingFestivo, setSavingFestivo] = useState(false);
  // Vacaciones
  const [modalVacaciones, setModalVacaciones] = useState(false);
  const [vacAgenteIds, setVacAgenteIds] = useState([]);
  const [vacForm, setVacForm] = useState({ fecha_inicio: '', fecha_fin: '', motivo: 'Vacaciones' });
  const [savingVac, setSavingVac] = useState(false);
  const [vacEditId, setVacEditId] = useState(null);
  const [modalGuardiasSabados, setModalGuardiasSabados] = useState(false);
  const [modalGuardiaDia, setModalGuardiaDia] = useState(false);
  /** fecha ISO (YYYY-MM-DD) → nombre agente guardia día (vista semana). */
  const [guardiasDiaPorFecha, setGuardiasDiaPorFecha] = useState({});
  /** fecha ISO del sábado → { iniciales, titulo } quien trabaja (vista semana). */
  const [guardiasSabadoPorFecha, setGuardiasSabadoPorFecha] = useState({});
  // Calendario visual de vacaciones
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [hoveredDate, setHoveredDate] = useState(null);
  // Calendario / hora rápida en modal cita
  const [citaCalYear, setCitaCalYear] = useState(new Date().getFullYear());
  const [citaCalMonth, setCitaCalMonth] = useState(new Date().getMonth());
  const [citaTcTarget, setCitaTcTarget] = useState("inicio");
  /** Con pestaña Inicio: 1er clic día = inicio, 2º = fin (reinicia al pulsar Inicio). */
  const citaCalAwaitFinRef = useRef(false);
  const [citaContactoBuscar, setCitaContactoBuscar] = useState("");
  const [citaContactoHits, setCitaContactoHits] = useState([]);
  const [citaContactoCargando, setCitaContactoCargando] = useState(false);
  const [citaPropBuscar, setCitaPropBuscar] = useState("");
  const [citaPropHits, setCitaPropHits] = useState([]);
  const [citaPropCargando, setCitaPropCargando] = useState(false);
  const [citaPropMeta, setCitaPropMeta] = useState({});
  const [fichaResumenPropiedadId, setFichaResumenPropiedadId] = useState(null);
  const [modalVisitaOpen, setModalVisitaOpen] = useState(false);
  const [visitaAgendaId, setVisitaAgendaId] = useState(null);
  const [visitaSnapshot, setVisitaSnapshot] = useState(null);
  const [visitaInitial, setVisitaInitial] = useState(null);
  const [visitaProperties, setVisitaProperties] = useState([]);
  const [visitaClienteTexto, setVisitaClienteTexto] = useState("");

  const fechaActualRef = useRef(fechaActual);
  const vistaRef = useRef(vista);
  const usuarioRef = useRef(usuarioSeleccionado);
  const usuariosAgendaRef = useRef(usuariosAgenda);
  useEffect(() => { usuariosAgendaRef.current = usuariosAgenda; }, [usuariosAgenda]);
  useEffect(() => { fechaActualRef.current = fechaActual; }, [fechaActual]);
  useEffect(() => { vistaRef.current = vista; }, [vista]);
  useEffect(() => { usuarioRef.current = usuarioSeleccionado; }, [usuarioSeleccionado]);

  const { usuarioPorId, agenteAUsuario } = useMemo(() => {
    const usuarioPorId = {};
    const agenteAUsuario = {};
    for (const u of usuariosAgenda) {
      usuarioPorId[String(u.id)] = u;
      if (u.agente_id && !agenteAUsuario[String(u.agente_id)]) {
        agenteAUsuario[String(u.agente_id)] = String(u.id);
      }
    }
    return { usuarioPorId, agenteAUsuario };
  }, [usuariosAgenda]);

  const citasVista = useMemo(
    () => citas.map((c) => enriquecerCitaAgenda(c, { usuarioPorId, isDark })),
    [citas, usuarioPorId, isDark]
  );

  const colorCitaVistaTodos = useCallback(
    (cita) => getColorCitaVistaTodos(cita, { usuarioPorId, agenteAUsuario }),
    [usuarioPorId, agenteAUsuario]
  );

  useEffect(() => {
    if (usuarioInicializadoRef.current || !usuariosAgenda.length) return;
    usuarioInicializadoRef.current = true;

    const mineAid = usuario?.agente_id ? String(usuario.agente_id) : "";
    const mineUser = mineAid
      ? usuariosAgenda.find((x) => x.agente_id && String(x.agente_id) === mineAid)
      : (usuario?.id ? usuariosAgenda.find((x) => String(x.id) === String(usuario.id)) : null);

    // Agente sin filtro / alcance propio: no restaurar otro usuario de localStorage
    // (deja la semana vacía al cruzarse con alcance_ver=propio en el API).
    if (forzarAgendaPropia && mineUser) {
      setUsuarioSeleccionado(String(mineUser.id));
      try { localStorage.setItem("agenda-usuario-id", String(mineUser.id)); } catch { /* ignore */ }
      return;
    }

    try {
      let saved = localStorage.getItem("agenda-usuario-id");
      if (!saved) {
        const legacy = localStorage.getItem("agenda-agente-id");
        if (legacy && legacy !== "todos") {
          const u = usuariosAgenda.find((x) => x.agente_id && String(x.agente_id) === String(legacy));
          if (u) saved = String(u.id);
        }
      }
      if (saved && saved !== "todos" && !usuariosAgenda.some((u) => String(u.id) === saved)) {
        try { localStorage.removeItem("agenda-usuario-id"); } catch { /* ignore */ }
        saved = null;
      }
      if (saved && (saved === "todos" || usuariosAgenda.some((u) => String(u.id) === saved))) {
        setUsuarioSeleccionado(saved);
        return;
      }
    } catch { /* ignore */ }
    if (mineUser) setUsuarioSeleccionado(String(mineUser.id));
  }, [usuariosAgenda, usuario?.id, usuario?.agente_id, forzarAgendaPropia]);

  // Cuando cargan los permisos (antes `puede` es permisivo), corregir selección ajena.
  useEffect(() => {
    if (!forzarAgendaPropia || !usuariosAgenda.length) return;
    const mineAid = usuario?.agente_id ? String(usuario.agente_id) : "";
    const mineUser = mineAid
      ? usuariosAgenda.find((x) => x.agente_id && String(x.agente_id) === mineAid)
      : (usuario?.id ? usuariosAgenda.find((x) => String(x.id) === String(usuario.id)) : null);
    if (!mineUser) return;
    if (String(usuarioSeleccionado) === String(mineUser.id)) return;
    setUsuarioSeleccionado(String(mineUser.id));
    try { localStorage.setItem("agenda-usuario-id", String(mineUser.id)); } catch { /* ignore */ }
  }, [forzarAgendaPropia, usuariosAgenda, usuario?.id, usuario?.agente_id, usuarioSeleccionado]);

  useEffect(() => {
    if (!usuarioInicializadoRef.current) return;
    try { localStorage.setItem("agenda-usuario-id", usuarioSeleccionado); } catch { /* ignore */ }
  }, [usuarioSeleccionado]);

  useEffect(() => { cargarUsuariosAgenda(); cargarAgentes(); cargarNoDisponible(); cargarFestivos(); }, []);
  useEffect(() => { cargarCitas(); }, [fechaActual, vista, usuarioSeleccionado]);

  useReloadOnRouteEnter(() => {
    cargarCitas();
  }, []);

  useEffect(() => {
    if (!(modalCita === "nueva" || (modalCita && modalCita.id))) return;
    setCitaTcTarget("inicio");
    citaCalAwaitFinRef.current = false;
  }, [modalCita === "nueva" ? "nueva" : modalCita?.id]);

  useEffect(() => {
    if (!(modalCita === "nueva" || (modalCita && modalCita.id))) return;
    const iso = citaTcTarget === "inicio" ? citaForm.fecha_inicio : citaForm.fecha_fin;
    if (iso && String(iso).length >= 10) {
      const d = new Date(String(iso).slice(0, 16));
      if (!isNaN(d.getTime())) {
        setCitaCalYear(d.getFullYear());
        setCitaCalMonth(d.getMonth());
      }
    } else {
      const n = new Date();
      setCitaCalYear(n.getFullYear());
      setCitaCalMonth(n.getMonth());
    }
  }, [modalCita, citaTcTarget, citaForm.fecha_inicio, citaForm.fecha_fin]);

  useEffect(() => {
    if (!(modalCita === "nueva" || (modalCita && modalCita.id))) {
      setCitaContactoBuscar("");
      setCitaContactoHits([]);
      setCitaPropBuscar("");
      setCitaPropHits([]);
      setCitaPropMeta({});
    }
  }, [modalCita]);

  useEffect(() => {
    if (!(modalCita === "nueva" || (modalCita && modalCita.id))) return;
    const q = citaContactoBuscar.trim();
    if (q.length < 2) {
      setCitaContactoHits([]);
      return;
    }
    const t = setTimeout(() => {
      (async () => {
        setCitaContactoCargando(true);
        try {
          const [rCli, rCnt] = await Promise.all([
            api.get("/clientes", { params: { buscar: q } }),
            api.get("/contactos", { params: { tipo: "todos", buscar: q } }),
          ]);
          const cli = (rCli.data?.data || []).slice(0, 12).map((row) => ({
            kind: "cliente",
            id: row.id,
            label: [row.nombre, row.apellido].filter(Boolean).join(" "),
            sub: [row.telefono, row.email].filter(Boolean).join(" · "),
            tag: "Cliente",
            telefono: row.telefono || "",
            apellido: row.apellido || "",
          }));
          const cnt = (rCnt.data?.data || []).slice(0, 12).map((row) => ({
            kind: "contacto",
            id: row.id,
            label: [row.nombre, row.apellidos].filter(Boolean).join(" "),
            sub: [row.telefono, row.email, row.poblacion].filter(Boolean).join(" · "),
            tag: row.tipo === "propietario" ? "Propietario" : "Colaborador / contacto",
            tipoRaw: row.tipo || "contacto",
            nom: row.nombre,
            apellidos: row.apellidos,
            telefono: row.telefono || "",
          }));
          setCitaContactoHits([...cli, ...cnt]);
        } catch (_) {
          setCitaContactoHits([]);
        } finally {
          setCitaContactoCargando(false);
        }
      })();
    }, 300);
    return () => clearTimeout(t);
  }, [citaContactoBuscar, modalCita]);

  useEffect(() => {
    if (!(modalCita === "nueva" || (modalCita && modalCita.id))) return;
    const q = citaPropBuscar.trim();
    if (q.length < 2) {
      setCitaPropHits([]);
      return;
    }
    const t = setTimeout(() => {
      (async () => {
        setCitaPropCargando(true);
        try {
          const res = await api.get("/propiedades", { params: { buscar: q, perPage: 15, cartera: "todas", page: 1 } });
          setCitaPropHits(res.data?.data || []);
        } catch (_) {
          setCitaPropHits([]);
        } finally {
          setCitaPropCargando(false);
        }
      })();
    }, 300);
    return () => clearTimeout(t);
  }, [citaPropBuscar, modalCita]);

  useEffect(() => {
    if (!(modalCita === "nueva" || (modalCita && modalCita.id))) return;
    const ids = Array.isArray(citaForm.propiedad_ids) ? [...new Set(citaForm.propiedad_ids.filter(Boolean))] : [];
    if (!ids.length) return;
    let cancel = false;
    (async () => {
      const rows = await Promise.all(
        ids.map((id) => api.get(`/propiedades/${id}`).then((res) => ({ id, p: res.data?.data })).catch(() => null))
      );
      if (cancel) return;
      setCitaPropMeta((prev) => {
        const next = { ...prev };
        rows.filter(Boolean).forEach(({ id, p }) => {
          if (p) next[id] = { referencia: p.referencia, titulo: p.titulo };
        });
        return next;
      });
    })();
    return () => { cancel = true; };
  }, [modalCita, modalCita?.id, citaForm.id, JSON.stringify(citaForm.propiedad_ids || [])]);

  useEffect(() => {
    const intervalo = setInterval(() => {
      cargarCitasConRefs();
      cargarNoDisponible();
    }, 30000);
    return () => clearInterval(intervalo);
  }, []);

  async function cargarUsuariosAgenda() {
    try {
      const res = await api.get("/agenda/usuarios");
      const lista = Array.isArray(res.data?.data) ? res.data.data
        : Array.isArray(res.data) ? res.data
        : [];
      setUsuariosAgenda(lista);
    } catch (e) { console.error("[Agenda] error usuarios agenda:", e); }
  }

  async function cargarAgentes() {
    try {
      const res = await api.get("/agentes");
      const lista = Array.isArray(res.data.data) ? res.data.data
                  : Array.isArray(res.data) ? res.data
                  : [];
      setAgentes(lista);
    } catch (e) { console.error("[Agenda] error agentes:", e); }
  }

  function agenteIdDelUsuarioSeleccionado() {
    if (usuarioSeleccionado === "todos") return null;
    const u = usuariosAgenda.find((x) => String(x.id) === String(usuarioSeleccionado));
    return u?.agente_id ? String(u.agente_id) : null;
  }

  function ndVisibleParaSeleccion(nd) {
    if (usuarioSeleccionado === "todos") return true;
    const aid = agenteIdDelUsuarioSeleccionado();
    if (!aid) return false;
    return String(nd.agente_id) === aid;
  }

  function usuarioIdParaCita(cita) {
    if (cita?.usuario_id != null) return String(cita.usuario_id);
    if (cita?.agente_id && agenteAUsuario[String(cita.agente_id)]) {
      return agenteAUsuario[String(cita.agente_id)];
    }
    return "";
  }

  function resolverAgenteParaUsuarioId(usuarioId) {
    if (usuarioId == null || usuarioId === "") return null;
    const u = usuariosAgenda.find((x) => String(x.id) === String(usuarioId));
    return u?.agente_id ? String(u.agente_id) : null;
  }

  function usuarioIdInicialNuevaCita(slotForm = {}) {
    if (slotForm.usuario_id) return String(slotForm.usuario_id);
    if (usuarioSeleccionado !== "todos") return String(usuarioSeleccionado);
    return usuario?.id ? String(usuario.id) : "";
  }

  function agenteIdInicialNuevaCita(slotForm = {}, usuarioId = "") {
    if (slotForm.agente_id) return String(slotForm.agente_id);
    const fromUsuario = resolverAgenteParaUsuarioId(usuarioId);
    if (fromUsuario) return fromUsuario;
    if (usuarioSeleccionado !== "todos") return agenteIdDelUsuarioSeleccionado();
    return usuario?.agente_id ? String(usuario.agente_id) : "";
  }

  function mapCitaToFormConUsuario(cita) {
    const base = mapCitaToForm(cita);
    const uid = usuarioIdParaCita(cita);
    return { ...base, usuario_id: uid };
  }

  function esCitaVisita(citaOrForm) {
    const tipo = String(citaOrForm?.tipo || "").toLowerCase();
    return tipo === "visita";
  }

  function esCitaCaptacion(citaOrForm) {
    const tipo = String(citaOrForm?.tipo || "").toLowerCase();
    return tipo === "captacion" || tipo === "valoracion";
  }

  function puedeCancelarCitaTipo(citaOrForm) {
    return esCitaVisita(citaOrForm) || esCitaCaptacion(citaOrForm);
  }

  function contactoModalVisita(snapshot = {}) {
    const nombreDesdeTitulo = snapshot.titulo
      ? String(snapshot.titulo).replace(/^Visita\s*[—-]\s*/i, "").trim()
      : "";
    const nombre =
      snapshot.cliente_nombre ||
      [snapshot.contacto_nombre, snapshot.contacto_apellidos].filter(Boolean).join(" ").trim() ||
      (nombreDesdeTitulo && !/^cliente$/i.test(nombreDesdeTitulo) ? nombreDesdeTitulo : "") ||
      "";
    const telefono = telefonoContactoCita(snapshot) || "";
    const email = snapshot.cliente_email || snapshot.contacto_email || "";
    return {
      nombre,
      telefono,
      email,
      iniciales: inicialesNombreApellido(nombre) || "?",
      clienteId: snapshot.cliente_id || null,
      contactoId: snapshot.contacto_id || null,
    };
  }

  const cerrarModalVisita = useCallback(() => {
    setModalVisitaOpen(false);
    setVisitaAgendaId(null);
    setVisitaSnapshot(null);
    setVisitaInitial(null);
    setVisitaProperties([]);
    setVisitaClienteTexto("");
  }, []);

  const abrirNuevaCitaAgenda = useCallback((slotForm = {}) => {
    const fi = slotForm.fecha_inicio || "";
    const ff = slotForm.fecha_fin || "";
    const dur = slotForm.duracion_minutos || 60;
    const usuarioInicial = usuarioIdInicialNuevaCita(slotForm);
    const agenteInicial = agenteIdInicialNuevaCita(slotForm, usuarioInicial);
    setCitaForm({
      tipo: slotForm.tipo || "captacion",
      estado: "pendiente",
      titulo: "",
      descripcion: "",
      fecha_inicio: fi || "",
      fecha_fin: ff || (fi ? finDesdeInicioYDuracion(fi, dur) : ""),
      duracion_minutos: dur,
      usuario_id: usuarioInicial,
      agente_id: agenteInicial,
      lead_id: slotForm.lead_id || null,
      cliente_id: slotForm.cliente_id || null,
      contacto_id: slotForm.contacto_id || null,
      captacion_id: slotForm.captacion_id || null,
      propiedad_ids: [],
      telefono_contacto: "",
    });
    setModalCita("nueva");
  }, [usuario?.id, usuario?.agente_id, usuarioSeleccionado, usuariosAgenda]);

  const abrirNuevaVisitaAgenda = useCallback((slotForm = {}) => {
    const fi = slotForm.fecha_inicio || "";
    const ff = slotForm.fecha_fin || "";
    const snap = {
      ...slotForm,
      tipo: "visita",
      captacion_id: null,
      id: undefined,
    };
    if (usuario?.id && !snap.usuario_id) snap.usuario_id = String(usuario.id);
    if (usuario?.agente_id && !snap.agente_id) snap.agente_id = String(usuario.agente_id);
    const { clienteTexto } = readAgendaTextoLibre(snap);
    setVisitaAgendaId(null);
    setVisitaSnapshot(snap);
    setVisitaClienteTexto(clienteTexto || nombreContactoCita(snap) || "");
    setVisitaInitial({
      fecha: fi.slice(0, 10) || fechaISO(new Date()),
      horaInicio: fi.includes("T") ? fi.slice(11, 16) : "10:00",
      horaFin: ff.includes("T") ? ff.slice(11, 16) : undefined,
      duracion: slotForm.duracion_minutos || 60,
    });
    setVisitaProperties([]);
    setModalVisitaOpen(true);
  }, [usuario?.id, usuario?.agente_id]);

  const abrirVisitaAgenda = useCallback(async (cita) => {
    if (!cita?.id) return;
    try {
      const { cita: loaded, propiedadesModal, initialValues, clienteTexto } = await loadCitaModalFromAgenda(
        api,
        cita.id,
        { mergeLeadRefs: false },
      );
      setVisitaAgendaId(String(loaded.id));
      setVisitaSnapshot({ ...loaded, ...mapCitaToFormConUsuario(loaded) });
      setVisitaClienteTexto(clienteTexto || nombreContactoCita(loaded) || "");
      setVisitaInitial(initialValues);
      setVisitaProperties(propiedadesModal);
      setModalVisitaOpen(true);
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || e?.message || "No se pudo abrir la visita.");
    }
  }, [agenteAUsuario, usuariosAgenda]);

  const handleVisitaConfirmAgenda = useCallback(async (data, options = {}) => {
    const propsVisita = data?.propiedades || [];
    const { reales, texto: propsTexto } = splitPropiedadesCita(propsVisita);
    const prev = options.snapshot ?? visitaSnapshot ?? {};
    const agendaId = options.agendaId ?? visitaAgendaId;
    const fechaInicioRaw = buildCitaDateTimeISO(data.fecha, data.horaInicio);
    const bloques = Math.max(1, propsVisita.length);
    const durMin = data.duracion || 45;
    const horaFin = data.horaFin || addMinutosHora(data.horaInicio, bloques * durMin);
    const fechaFinRaw = buildCitaDateTimeISO(data.fecha, horaFin);
    const { fecha_inicio: fechaInicio, fecha_fin: fechaFin } = ensureVisitaRangoISO(
      fechaInicioRaw,
      fechaFinRaw,
      { bloques, duracionMin: durMin },
    );
    if (!fechaInicio || !fechaFin) throw new Error("Fecha u hora de la visita no válidas.");
    const propiedadIds = reales
      .map((p) => p.id)
      .filter((id) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(id || "")));
    const clienteLibre = String(data.clienteTexto || "").trim();
    const contacto = contactoModalVisita(prev);
    const nombreTitulo = clienteLibre || contacto.nombre || "";
    const lineas = propsVisita.map((p) => {
      const label = p.referencia || p.titulo || p.id;
      const marca = p.esTextoLibre || String(p.id || "").startsWith("txt:") ? " (texto)" : "";
      return `${p.horaVisita || data.horaInicio} · ${label}${marca}`;
    });
    const metaPrev = parseMetadataMovil(prev.metadata_movil);
    const metadata_movil = {
      ...metaPrev,
      cliente_texto: clienteLibre || null,
      propiedades_texto: propsTexto.length ? propsTexto : null,
    };
    if (!metadata_movil.cliente_texto) delete metadata_movil.cliente_texto;
    if (!metadata_movil.propiedades_texto) delete metadata_movil.propiedades_texto;

    // Si el usuario escribe un nombre a mano y no hay ficha CRM, no inventamos cliente_id.
    const sinFichaCrm = !prev.cliente_id && !prev.contacto_id && !prev.lead_id;
    const payload = {
      usuario_id: prev.usuario_id || (usuario?.id ? String(usuario.id) : null),
      agente_id: prev.agente_id || (usuario?.agente_id ? String(usuario.agente_id) : null),
      lead_id: prev.lead_id || null,
      cliente_id: sinFichaCrm && clienteLibre ? null : (prev.cliente_id || null),
      contacto_id: sinFichaCrm && clienteLibre ? null : (prev.contacto_id || null),
      captacion_id: null,
      titulo: prev.titulo && !clienteLibre
        ? prev.titulo
        : (nombreTitulo ? `Visita — ${nombreTitulo}` : "Visita"),
      descripcion: propsVisita.length
        ? `Visita programada.\n${lineas.join("\n")}`
        : (prev.descripcion || "Visita programada."),
      tipo: "visita",
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      estado: prev.estado || "pendiente",
      direccion: prev.direccion || null,
      notas: prev.notas || null,
      propiedad_ids: propiedadIds.length ? propiedadIds : null,
      metadata_movil,
      creado_por_ia: prev.creado_por_ia || false,
      confirmado_por_cliente: prev.confirmado_por_cliente || false,
    };
    if (agendaId) {
      await api.put(`/agenda/${agendaId}`, { id: agendaId, ...payload });
    } else {
      await api.post("/agenda", payload);
    }
    if (!options.skipClose) cerrarModalVisita();
    await cargarCitas();
  }, [visitaSnapshot, visitaAgendaId, usuario?.id, usuario?.agente_id, cerrarModalVisita]);

  const editCitaParam = searchParams.get("edit");
  useEffect(() => {
    if (!editCitaParam) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/agenda/${editCitaParam}`);
        const cita = res.data?.data || res.data;
        if (cancelled || !cita?.id) return;
        if (esCitaVisita(cita)) {
          await abrirVisitaAgenda(cita);
        } else {
          setCitaForm(mapCitaToFormConUsuario(cita));
          setModalCita(cita);
        }
        const next = new URLSearchParams(searchParams);
        next.delete("edit");
        setSearchParams(next, { replace: true });
      } catch (e) {
        console.error("No se pudo abrir la cita desde historial:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [editCitaParam]);

  function paramsFiltroAgenda(usuarioId) {
    const params = {};
    if (!usuarioId || usuarioId === "todos") return params;
    const aid = (() => {
      const u = usuariosAgendaRef.current.find((x) => String(x.id) === String(usuarioId));
      return u?.agente_id ? String(u.agente_id) : null;
    })();
    if (aid) params.agente_id = aid;
    else params.usuario_id = usuarioId;
    return params;
  }

  const cargarCitasSeqRef = useRef(0);
  async function cargarCitas() {
    const seq = ++cargarCitasSeqRef.current;
    try {
      setLoading(true);
      const { inicio, fin } = getRangoFechas(fechaActual, vista);
      const params = { fecha_inicio: inicio, fecha_fin: fin, ...paramsFiltroAgenda(usuarioSeleccionado) };
      const res = await api.get("/agenda", { params });
      if (seq !== cargarCitasSeqRef.current) return;
      setCitas(res.data?.data ?? res.data ?? []);
    } catch (e) {
      if (seq !== cargarCitasSeqRef.current) return;
      console.error("[Agenda] error cargar citas:", e?.response?.data?.error || e?.message || e);
      setCitas([]);
    } finally {
      if (seq === cargarCitasSeqRef.current) setLoading(false);
    }
  }

  async function cargarCitasConRefs() {
    if (interactuandoCitaRef.current) return;
    try {
      const { inicio, fin } = getRangoFechas(fechaActualRef.current, vistaRef.current);
      const params = { fecha_inicio: inicio, fecha_fin: fin, ...paramsFiltroAgenda(usuarioRef.current) };
      const res = await api.get("/agenda", { params });
      setCitas(res.data?.data ?? res.data ?? []);
    } catch (e) {
      console.error("[Agenda] error cargar citas (poll):", e);
    }
  }

  async function cargarNoDisponible() {
    try {
      const res = await api.get("/agenda-no-disponible");
      setNoDisponible(res.data.data || []);
    } catch (e) { console.error(e); }
  }

  async function cargarFestivos() {
    try {
      const res = await api.get("/festivos");
      setFestivos(res.data.data || []);
    } catch (e) { console.error(e); }
  }

  const cargarGuardiasDiaSemana = useCallback(async () => {
    if (vista !== "semana") {
      setGuardiasDiaPorFecha({});
      setGuardiasSabadoPorFecha({});
      return;
    }
    try {
      const dow = (fechaActual.getDay() + 6) % 7;
      const lun = new Date(fechaActual);
      lun.setDate(fechaActual.getDate() - dow);
      const years = new Set();
      for (let i = 0; i < 7; i += 1) {
        const dd = new Date(lun);
        dd.setDate(lun.getDate() + i);
        years.add(dd.getFullYear());
      }
      const map = {};
      const mapSab = {};
      for (const y of years) {
        const [resDia, resSab] = await Promise.all([
          api.get("/guardias-dia", { params: { year: y } }),
          api.get("/guardias-sabado", { params: { year: y } }),
        ]);
        for (const r of resDia.data?.data || []) {
          if (r.agente_nombre) map[r.fecha] = r.agente_nombre;
        }
        for (const r of resSab.data?.data || []) {
          const etiqueta = etiquetaGuardiaSabadoSemana(r);
          if (etiqueta) mapSab[r.fecha] = etiqueta;
        }
      }
      setGuardiasDiaPorFecha(map);
      setGuardiasSabadoPorFecha(mapSab);
    } catch (e) {
      console.error(e);
      setGuardiasDiaPorFecha({});
      setGuardiasSabadoPorFecha({});
    }
  }, [vista, fechaActual]);

  useEffect(() => {
    cargarGuardiasDiaSemana();
  }, [cargarGuardiasDiaSemana]);

  function esFestivo(dateStr) {
    return festivos.some(f => f.fecha === dateStr && f.activo);
  }

  /** Domingos y festivos: rojo en cabecera mes/semana; el cuerpo semana usa franja 09:30–19:00. */
  function esDiaNoLaborable(date) {
    const dStr = fechaISO(date);
    return esFestivo(dStr) || date.getDay() === 0;
  }

  function getDayCellBgSemanaGrid(date) {
    const dow = date.getDay();
    if (isDark) return "transparent";
    if (dow === 6) return "#f1f5f9";
    return "#ffffff";
  }

  function getDayCellBg(date) {
    const dStr = fechaISO(date);
    const dow = date.getDay();
    if (esFestivo(dStr) || dow === 0) return COLOR_BOTON_FESTIVOS;
    if (isDark) return "transparent";
    if (dow === 6) return "#f1f5f9";
    return "#ffffff";
  }

  function getDayHeaderBg(date) {
    const dStr = fechaISO(date);
    const dow = date.getDay();
    if (esFestivo(dStr) || dow === 0) return COLOR_BOTON_FESTIVOS;
    if (isDark) return "transparent";
    if (dow === 6) return "#e2e8f0";
    return "transparent";
  }

  // Funciones para festivos CRUD
  async function guardarFestivo() {
    setSavingFestivo(true);
    try {
      if (editFestivo?.id) {
        const res = await api.put(`/festivos/${editFestivo.id}`, festivoForm);
        setFestivos(prev => prev.map(f => f.id === editFestivo.id ? res.data.data : f));
      } else {
        const res = await api.post("/festivos", festivoForm);
        setFestivos(prev => [...prev, res.data.data]);
      }
      setEditFestivo(null);
      setFestivoForm({ fecha: '', nombre: '', ambito: 'local' });
    } catch (e) { console.error(e); await dialogs.alert(e.response?.data?.error || 'Error al guardar'); }
    finally { setSavingFestivo(false); }
  }

  async function eliminarFestivo(id) {
    if (!(await dialogs.confirm('¿Eliminar este festivo?'))) return;
    await api.delete(`/festivos/${id}`);
    setFestivos(prev => prev.filter(f => f.id !== id));
  }

  async function toggleFestivo(f) {
    const res = await api.put(`/festivos/${f.id}`, { ...f, activo: !f.activo });
    setFestivos(prev => prev.map(x => x.id === f.id ? res.data.data : x));
  }

  async function seedFestivosYear(year) {
    try {
      await api.post(`/festivos/seed/${year}`);
      const res = await api.get("/festivos");
      setFestivos(res.data.data || []);
      await dialogs.alert(`Festivos ${year} cargados`);
    } catch (e) { await dialogs.alert(e.response?.data?.error || 'Error'); }
  }

  function iniciarEdicionVacaciones(nd) {
    const fi = nd.fecha_inicio?.slice(0, 10) || '';
    const ff = nd.fecha_fin?.slice(0, 10) || '';
    setVacEditId(nd.id);
    setVacForm({ fecha_inicio: fi, fecha_fin: ff, motivo: nd.motivo || 'Vacaciones' });
    setVacAgenteIds(nd.agente_id ? [nd.agente_id] : []);
    if (fi) {
      const d = new Date(`${fi}T12:00:00`);
      if (!isNaN(d.getTime())) {
        setCalYear(d.getFullYear());
        setCalMonth(d.getMonth());
      }
    }
    setHoveredDate(null);
  }

  function descartarEdicionVacaciones() {
    setVacEditId(null);
    setVacForm({ fecha_inicio: '', fecha_fin: '', motivo: 'Vacaciones' });
    setVacAgenteIds([]);
    setHoveredDate(null);
  }

  // Funciones para vacaciones
  async function guardarVacaciones() {
    if (!vacForm.fecha_inicio || !vacForm.fecha_fin) {
      await dialogs.alert('Selecciona las fechas de inicio y fin'); return;
    }

    if (vacEditId) {
      const nd = noDisponible.find(x => x.id === vacEditId);
      if (!nd) {
        await dialogs.alert('No se encontró el período a editar');
        setVacEditId(null);
        return;
      }
      const agente_id = vacAgenteIds[0] || nd.agente_id;
      if (!agente_id) {
        await dialogs.alert('Selecciona el comercial del período');
        return;
      }
      const todoDia = nd.todo_el_dia === true || nd.todo_el_dia === 't' || nd.todo_el_dia === 'true';
      setSavingVac(true);
      try {
        await api.put(`/agenda-no-disponible/${vacEditId}`, {
          agente_id,
          motivo: vacForm.motivo || 'Vacaciones',
          tipo: nd.tipo || 'vacaciones',
          fecha_inicio: vacForm.fecha_inicio,
          fecha_fin: vacForm.fecha_fin,
          hora_inicio: nd.hora_inicio || null,
          hora_fin: nd.hora_fin || null,
          todo_el_dia: todoDia,
        });
        descartarEdicionVacaciones();
        cargarNoDisponible();
      } catch (e) {
        console.error(e);
        await dialogs.alert(e.response?.data?.error || 'Error al guardar los cambios');
      } finally {
        setSavingVac(false);
      }
      return;
    }

    if (vacAgenteIds.length === 0) {
      await dialogs.alert('Selecciona al menos un comercial y las fechas');
      return;
    }
    setSavingVac(true);
    try {
      for (const agente_id of vacAgenteIds) {
        await api.post("/agenda-no-disponible", {
          agente_id, motivo: vacForm.motivo || 'Vacaciones',
          tipo: 'vacaciones', fecha_inicio: vacForm.fecha_inicio,
          fecha_fin: vacForm.fecha_fin, todo_el_dia: true,
        });
      }
      descartarEdicionVacaciones();
      cargarNoDisponible();
    } catch (e) {
      console.error(e);
      await dialogs.alert(e.response?.data?.error || 'Error al guardar');
    } finally {
      setSavingVac(false);
    }
  }

  function handleVacDateClick(dateStr) {
    const { fecha_inicio, fecha_fin } = vacForm;
    if (!fecha_inicio || (fecha_inicio && fecha_fin)) {
      setVacForm(p => ({ ...p, fecha_inicio: dateStr, fecha_fin: '' }));
      setHoveredDate(null);
    } else {
      if (dateStr >= fecha_inicio) {
        setVacForm(p => ({ ...p, fecha_fin: dateStr }));
        setHoveredDate(null);
      } else {
        setVacForm(p => ({ ...p, fecha_inicio: dateStr, fecha_fin: '' }));
      }
    }
  }

  function renderVacCalendarDays() {
    const { fecha_inicio, fecha_fin } = vacForm;
    const firstDay = new Date(calYear, calMonth, 1);
    const lastDay = new Date(calYear, calMonth + 1, 0);
    const startDow = (firstDay.getDay() + 6) % 7;
    const effEnd = fecha_fin || (fecha_inicio && hoveredDate && hoveredDate >= fecha_inicio ? hoveredDate : null);

    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) cells.push(new Date(calYear, calMonth, d));

    return cells.map((date, i) => {
      if (!date) return <div key={i} style={{ height: 38 }} />;
      const dStr = fechaISO(date);
      const isStart = dStr === fecha_inicio;
      const isEnd = effEnd && dStr === effEnd;
      const sameDay = fecha_inicio && effEnd && fecha_inicio === effEnd;
      const inRange = fecha_inicio && effEnd && dStr > fecha_inicio && dStr < effEnd;
      const isFest = esFestivo(dStr);
      const isSunday = date.getDay() === 0;
      const isToday = dStr === fechaISO(new Date());

      const outerBg = sameDay && isStart ? 'transparent'
        : isStart && effEnd ? 'linear-gradient(to right, transparent 50%, #fde68a 50%)'
        : isEnd ? 'linear-gradient(to left, transparent 50%, #fde68a 50%)'
        : inRange ? '#fde68a'
        : 'transparent';

      const circBg = isStart || isEnd ? '#f59e0b' : isToday ? (isDark ? '#ffffff22' : '#e8f0f8') : 'transparent';
      const circBorder = isToday && !isStart && !isEnd ? `2px solid ${isDark ? '#ffffff44' : '#1a3a5c'}` : 'none';
      const textColor = isStart || isEnd ? '#ffffff'
        : inRange ? (isDark ? '#fcd34d' : '#92400e')
        : isFest || isSunday ? '#b91c1c'
        : c.textPrimary;

      return (
        <div key={i} onClick={() => handleVacDateClick(dStr)}
          onMouseEnter={() => { if (fecha_inicio && !fecha_fin) setHoveredDate(dStr); }}
          onMouseLeave={() => setHoveredDate(null)}
          style={{ height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', background: outerBg, cursor: 'pointer' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: circBg, border: circBorder, fontSize: 13, fontWeight: isStart || isEnd ? 700 : 400, color: textColor, userSelect: 'none' }}>
            {date.getDate()}
          </div>
        </div>
      );
    });
  }

  function pickTimeFromField(cur, fallbackH, fallbackM) {
    let timePart = `${pad(fallbackH)}:${pad(fallbackM)}`;
    if (cur.includes("T")) {
      const parts = cur.split("T")[1].split(":");
      timePart = `${pad(parseInt(parts[0], 10) || fallbackH)}:${pad(parseInt(parts[1], 10) || fallbackM)}`;
    }
    return timePart;
  }

  function handleCitaCalDayClick(dateStr) {
    if (citaTcTarget === "fin") {
      citaCalAwaitFinRef.current = false;
      setCitaForm((p) => {
        const cur = p.fecha_fin || "";
        const timePart = pickTimeFromField(cur, 9, 0);
        return { ...p, fecha_fin: `${dateStr}T${timePart}` };
      });
      return;
    }
    if (!citaCalAwaitFinRef.current) {
      citaCalAwaitFinRef.current = true;
      setCitaForm((p) => {
        const cur = p.fecha_inicio || "";
        const timePart = pickTimeFromField(cur, 9, 0);
        return { ...p, fecha_inicio: `${dateStr}T${timePart}` };
      });
      return;
    }
    citaCalAwaitFinRef.current = false;
    setCitaTcTarget("fin");
    setCitaForm((p) => {
      const curFin = p.fecha_fin || "";
      const curIni = p.fecha_inicio || "";
      const tp = curFin.includes("T")
        ? pickTimeFromField(curFin, 9, 0)
        : pickTimeFromField(curIni, 9, 30);
      return { ...p, fecha_fin: `${dateStr}T${tp}` };
    });
  }

  function applyCitaHM(h, m) {
    setCitaForm((p) => {
      const key = citaTcTarget === "inicio" ? "fecha_inicio" : "fecha_fin";
      const cur = p[key] || "";
      let d = cur.slice(0, 10);
      if (!d) d = fechaISO(new Date());
      const next = { ...p, [key]: `${d}T${pad(h)}:${pad(m)}` };
      if (key === "fecha_fin" && next.fecha_inicio) {
        next.duracion_minutos = minutosDuracionCita({ fecha_inicio: next.fecha_inicio, fecha_fin: next.fecha_fin });
      }
      return next;
    });
  }

  function applyCitaDuracionRapida(mins) {
    setCitaForm((p) => ({
      ...p,
      duracion_minutos: mins,
      fecha_fin: finDesdeInicioYDuracion(p.fecha_inicio, mins),
    }));
  }

  function applyCitaQuickHour(h) {
    setCitaForm((p) => {
      const key = citaTcTarget === "inicio" ? "fecha_inicio" : "fecha_fin";
      const cur = p[key] || "";
      let d = cur.slice(0, 10);
      let m = 0;
      if (cur.includes("T")) {
        const pr = cur.split("T")[1].split(":");
        m = parseInt(pr[1], 10);
        if (!Number.isFinite(m)) m = 0;
      }
      if (!d) d = fechaISO(new Date());
      return { ...p, [key]: `${d}T${pad(h)}:${pad(m)}` };
    });
  }

  function applyCitaQuickMin(minutes) {
    setCitaForm((p) => {
      const key = citaTcTarget === "inicio" ? "fecha_inicio" : "fecha_fin";
      const cur = p[key] || "";
      let d = cur.slice(0, 10);
      let ho = 9;
      if (cur.includes("T")) {
        const pr = cur.split("T")[1].split(":");
        ho = parseInt(pr[0], 10);
        if (!Number.isFinite(ho)) ho = 9;
      }
      if (!d) d = fechaISO(new Date());
      return { ...p, [key]: `${d}T${pad(ho)}:${pad(minutes)}` };
    });
  }

  function renderCitaCalendarDays() {
    const rawI = citaForm.fecha_inicio || "";
    const rawF = citaForm.fecha_fin || "";
    const startDate = rawI.length >= 10 ? rawI.slice(0, 10) : "";
    const endDate = rawF.length >= 10 ? rawF.slice(0, 10) : "";

    const firstDay = new Date(citaCalYear, citaCalMonth, 1);
    const lastDay = new Date(citaCalYear, citaCalMonth + 1, 0);
    const startDow = (firstDay.getDay() + 6) % 7;

    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let day = 1; day <= lastDay.getDate(); day++) cells.push(new Date(citaCalYear, citaCalMonth, day));

    return cells.map((date, i) => {
      if (!date) return <div key={`ce${i}`} style={{ height: 30 }} />;
      const dStr = fechaISO(date);
      const isStart = startDate && dStr === startDate;
      const isEnd = endDate && dStr === endDate;
      const isFest = esFestivo(dStr);
      const isSunday = date.getDay() === 0;
      const isToday = dStr === fechaISO(new Date());

      let circBg = "transparent";
      if (isStart && isEnd) circBg = "#f59e0b";
      else if (isStart) circBg = "#f59e0b";
      else if (isEnd) circBg = "#0284c7";
      else if (isToday) circBg = isDark ? mezclarColor("#3b82f6", "#1e293b", 0.45) : "#e8f0f8";

      const ring = isStart && isEnd;
      const circBorder = ring
        ? `2px solid ${isDark ? "#fcd34d" : "#fff8e1"}`
        : isToday && !isStart && !isEnd
          ? `2px solid ${isDark ? "#94a3b8" : "#1a3a5c"}`
          : "none";
      const textColor = isStart || isEnd ? "#ffffff" : isFest || isSunday ? "#b91c1c" : c.textPrimary;

      return (
        <div key={i} onClick={() => handleCitaCalDayClick(dStr)} style={{ height: 30, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", cursor: "pointer" }}>
          <div style={{ width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: circBg, border: circBorder, fontSize: 11, fontWeight: isStart || isEnd ? 700 : 500, color: textColor, userSelect: "none" }}>
            {date.getDate()}
          </div>
        </div>
      );
    });
  }

  function getRangoFechas(fecha, v) {
    const d = new Date(fecha);
    if (v === "dia") {
      return { inicio: fechaISO(d) + "T00:00:00", fin: fechaISO(d) + "T23:59:59" };
    } else if (v === "semana") {
      const dow = (d.getDay() + 6) % 7;
      const lunes = new Date(d); lunes.setDate(d.getDate() - dow);
      const domingo = new Date(lunes); domingo.setDate(lunes.getDate() + 6);
      return { inicio: fechaISO(lunes) + "T00:00:00", fin: fechaISO(domingo) + "T23:59:59" };
    } else {
      const inicio = new Date(d.getFullYear(), d.getMonth(), 1);
      const fin = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      return { inicio: fechaISO(inicio) + "T00:00:00", fin: fechaISO(fin) + "T23:59:59" };
    }
  }

  function navegar(dir) {
    const d = new Date(fechaActual);
    if (vista === "dia") d.setDate(d.getDate() + dir);
    else if (vista === "semana") d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setFechaActual(d);
  }

  function getTituloNavegacion() {
    if (vista === "dia") return `${fechaActual.getDate()} de ${MESES[fechaActual.getMonth()]} ${fechaActual.getFullYear()}`;
    if (vista === "semana") {
      const dow = (fechaActual.getDay() + 6) % 7;
      const lunes = new Date(fechaActual); lunes.setDate(fechaActual.getDate() - dow);
      const domingo = new Date(lunes); domingo.setDate(lunes.getDate() + 6);
      return `${lunes.getDate()} ${MESES[lunes.getMonth()]} — ${domingo.getDate()} ${MESES[domingo.getMonth()]} ${domingo.getFullYear()}`;
    }
    return `${MESES[fechaActual.getMonth()]} ${fechaActual.getFullYear()}`;
  }

  const verTodosUsuarios = usuarioSeleccionado === "todos";
  function getColorAgente(agente_id) { return getColorAgenteAgenda(agente_id); }
  function estiloBloqueCita(cita) {
    return getEstiloCitaAgenda(
      cita,
      verTodosUsuarios,
      isDark,
      verTodosUsuarios ? colorCitaVistaTodos : undefined
    );
  }
  function attrsCajaCita(cita, estilo, { arrastre = false } = {}) {
    const box = estilosCajaCita(estilo, cita, { verTodosAgentes: verTodosUsuarios, isDark, arrastre });
    if (box.className) {
      return { className: box.className, "data-tipo": box["data-tipo"], style: box.style };
    }
    return { style: box };
  }
  /** Iniciales nombre + 1.er apellido (avatares cabecera semana). */
  function inicialNombreAgente(nombre) {
    return inicialesNombreApellido(nombre);
  }
  function citasFiltradas() {
    return citasVista.filter((ct) => filtroTipo === "todos" || ct.tipo === filtroTipo);
  }

  const telefonoModalCita = () =>
    (citaForm.telefono_contacto && String(citaForm.telefono_contacto).trim()) ||
    telefonoContactoCita(citaForm) ||
    "";

  const llamarDesdeCita = async (telefono) => {
    const tel = String(telefono || "").trim();
    if (!tel) {
      await dialogs.alert("No hay teléfono de contacto");
      return;
    }
    try {
      const data = await api.post("/telnyx/llamar", {
        telefonoCliente: tel,
        clienteId: citaForm.cliente_id || null,
        leadId: citaForm.lead_id || null,
      }).then((r) => r.data);
      if (data.ok) {
        await dialogs.alert("📞 Llamada iniciada — tu móvil recibirá la llamada en breve");
      } else {
        await dialogs.alert("Error: " + (data.error || "No se pudo iniciar la llamada"));
      }
    } catch (e) {
      await dialogs.alert("Error: " + (e.response?.data?.error || e.message));
    }
  };

  const llamarDesdeVisita = useCallback(async (telefono, snapshot = visitaSnapshot) => {
    const tel = String(telefono || "").trim();
    if (!tel) {
      await dialogs.alert("No hay teléfono de contacto");
      return;
    }
    const snap = snapshot || {};
    try {
      const data = await api.post("/telnyx/llamar", {
        telefonoCliente: tel,
        clienteId: snap.cliente_id || null,
        leadId: snap.lead_id || null,
      }).then((r) => r.data);
      if (data.ok) {
        await dialogs.alert("📞 Llamada iniciada — tu móvil recibirá la llamada en breve");
      } else {
        await dialogs.alert("Error: " + (data.error || "No se pudo iniciar la llamada"));
      }
    } catch (e) {
      await dialogs.alert("Error: " + (e.response?.data?.error || e.message));
    }
  }, [visitaSnapshot]);

  const transferVisitaModalAFicha = useCallback((path) => {
    if (!path) return;
    if (!citaOverlay) {
      navigate(path);
      return;
    }
    const snapshot = visitaSnapshot;
    const agendaId = visitaAgendaId;
    citaOverlay.transferModalToFicha({
      path,
      returnTo: `${location.pathname}${location.search}`,
      onCloseLocal: cerrarModalVisita,
      modalProps: {
        onConfirm: (data) => handleVisitaConfirmAgenda(data, { snapshot, agendaId, skipClose: true }),
        modoEdicion: Boolean(agendaId),
        initialValues: visitaInitial,
        contact: contactoModalVisita(snapshot || {}),
        properties: visitaProperties,
        agentName: snapshot?.agente_nombre || "",
        onLlamarTelefono: (tel) => llamarDesdeVisita(tel, snapshot),
        onBuscarPropiedadPorRef: (ref) => fetchPropiedadPorRef(api, ref),
        footerExtra: agendaId ? (
          <>
            <button
              type="button"
              onClick={async () => {
                if (!(await dialogs.confirm("¿Eliminar esta cita?"))) return;
                await api.delete(`/agenda/${agendaId}`);
                citaOverlay.clearOverlay({ navigateReturn: true });
              }}
              style={{ background: "#EF444422", border: "1px solid #EF4444", color: "#EF4444", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12 }}
            >
              Eliminar
            </button>
            <button
              type="button"
              onClick={() => {
                citaOverlay.clearOverlay();
                navigate("/agenda", { state: { feedbackAgendaId: String(agendaId) } });
              }}
              style={{ background: "linear-gradient(135deg, #0369a1, #0ea5e9)", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
            >
              Resultado de la visita
            </button>
          </>
        ) : null,
      },
    });
  }, [
    citaOverlay,
    navigate,
    location.pathname,
    location.search,
    visitaSnapshot,
    visitaAgendaId,
    visitaInitial,
    visitaProperties,
    cerrarModalVisita,
    handleVisitaConfirmAgenda,
    llamarDesdeVisita,
  ]);

  async function guardarCita() {
    if (!citaForm.fecha_inicio || !citaForm.fecha_fin) {
      await dialogs.alert("Indica fecha y hora de inicio y de fin.");
      return;
    }
    try {
      const toUTC = (v) => {
        if (!v) return undefined;
        const d = new Date(v);
        if (isNaN(d.getTime())) return undefined;
        return d.toISOString();
      };
      const propiedad_ids =
        Array.isArray(citaForm.propiedad_ids) && citaForm.propiedad_ids.length
          ? citaForm.propiedad_ids.filter(Boolean)
          : null;
      let usuarioGuardar = null;
      let agenteGuardar = null;
      const agentePrevio =
        citaForm.agente_id ||
        (modalCita && modalCita !== "nueva" ? modalCita.agente_id : null) ||
        null;
      if (citaForm.usuario_id === "") {
        usuarioGuardar = null;
        agenteGuardar = agentePrevio ? String(agentePrevio) : null;
      } else if (citaForm.usuario_id) {
        usuarioGuardar = String(citaForm.usuario_id);
        const uAsig = usuariosAgenda.find((x) => String(x.id) === usuarioGuardar);
        agenteGuardar = uAsig?.agente_id
          ? String(uAsig.agente_id)
          : (agentePrevio ? String(agentePrevio) : null);
      } else if (modalCita === "nueva" && usuario?.id) {
        usuarioGuardar = String(usuario.id);
        agenteGuardar = agentePrevio
          ? String(agentePrevio)
          : (usuario?.agente_id ? String(usuario.agente_id) : null);
      } else if (agentePrevio) {
        agenteGuardar = String(agentePrevio);
      }

      const metaPrev = parseMetadataMovil(citaForm.metadata_movil);
      const clienteLibre = String(citaForm.cliente_texto || "").trim();
      const propsTexto = Array.isArray(citaForm.propiedades_texto)
        ? citaForm.propiedades_texto.map((x) => String(x || "").trim()).filter(Boolean)
        : [];
      const metadata_movil = { ...metaPrev };
      if (clienteLibre) metadata_movil.cliente_texto = clienteLibre;
      else delete metadata_movil.cliente_texto;
      if (propsTexto.length) metadata_movil.propiedades_texto = propsTexto;
      else delete metadata_movil.propiedades_texto;

      const citaData = {
        id: citaForm.id,
        usuario_id: usuarioGuardar,
        agente_id: agenteGuardar,
        lead_id: citaForm.lead_id || null,
        cliente_id: citaForm.cliente_id || null,
        contacto_id: citaForm.contacto_id || null,
        captacion_id: citaForm.captacion_id || null,
        titulo: citaForm.titulo?.trim()
          ? citaForm.titulo.trim()
          : (clienteLibre || null),
        descripcion: citaForm.descripcion,
        tipo: citaForm.tipo,
        fecha_inicio: toUTC(citaForm.fecha_inicio),
        fecha_fin: toUTC(citaForm.fecha_fin),
        estado: citaForm.estado,
        direccion: citaForm.direccion,
        notas: citaForm.notas,
        propiedad_ids,
        metadata_movil,
        creado_por_ia: citaForm.creado_por_ia,
        confirmado_por_cliente: citaForm.confirmado_por_cliente,
      };
      if (citaData.id) {
        await api.put(`/agenda/${citaData.id}`, citaData);
      } else {
        await api.post("/agenda", citaData);
      }
      const tel = (citaForm.telefono_contacto || "").trim();
      if (tel) {
        try {
          if (citaForm.cliente_id) {
            await api.patch(`/clientes/${citaForm.cliente_id}`, { telefono: tel });
          } else if (citaForm.contacto_id) {
            await api.patch(`/contactos/${citaForm.contacto_id}`, { telefono: tel });
          }
        } catch (telErr) {
          console.warn("[Agenda] no se pudo actualizar teléfono del contacto:", telErr);
        }
      }
      setModalCita(null);
      setCitaForm({});
      await cargarCitas();
    } catch (e) { console.error(e); }
  }

  const guardarCitaArrastre = useCallback(async (cita, fecha_inicio, fecha_fin) => {
    try {
      await api.put(`/agenda/${cita.id}`, { fecha_inicio, fecha_fin });
      await cargarCitas();
    } catch (e) {
      console.error(e);
      await dialogs.alert(
        "No se pudo actualizar la cita: " + (e.response?.data?.error || e.message || "Error")
      );
    }
  }, []);

  const {
    registrarColumna,
    getColumnaEl,
    iniciarMover,
    iniciarResize,
    activaId,
    huboMovimientoRef,
    citaConPreview,
  } = useAgendaCitaPointer({
    onGuardar: guardarCitaArrastre,
    guardandoRef: guardandoDragRef,
    interactuandoRef: interactuandoCitaRef,
  });

  async function eliminarCita(id) {
    if (!(await dialogs.confirm("¿Eliminar esta cita?"))) return;
    await api.delete(`/agenda/${id}`);
    setModalCita(null);
    setCitaForm({});
    if (visitaAgendaId && String(visitaAgendaId) === String(id)) cerrarModalVisita();
    cargarCitas();
  }

  async function cancelarCita(id) {
    const tipo = String(modalCita?.tipo || citaForm?.tipo || "").toLowerCase();
    const esCap = tipo === "captacion" || tipo === "valoracion";
    const msg = esCap
      ? "¿Cancelar esta cita?\n\nSe marcará como cancelada y la captación volverá a Contactados si estaba en Visita concertada."
      : "¿Cancelar esta cita?\n\nSe marcará como cancelada, se cerrará la tarea de la visita y el lead volverá a Atendidos.";
    if (!(await dialogs.confirm(msg))) return;
    try {
      const { data } = await api.post(`/agenda/${id}/cancelar`);
      if (data?.ok === false) {
        await dialogs.alert(data?.error || "No se pudo cancelar la cita.");
        return;
      }
      setModalCita(null);
      setCitaForm({});
      if (visitaAgendaId && String(visitaAgendaId) === String(id)) cerrarModalVisita();
      cargarCitas();
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || e?.message || "No se pudo cancelar la cita.");
    }
  }

  async function guardarNoDisp() {
    try {
      await api.post("/agenda-no-disponible", ndFormToApi(noDispForm));
      setModalNoDisp(false);
      setNoDispForm({});
      cargarNoDisponible();
    } catch (e) { console.error(e); }
  }

  async function eliminarNoDisp(id) {
    if (!(await dialogs.confirm("¿Eliminar este período de no disponibilidad?"))) return;
    await api.delete(`/agenda-no-disponible/${id}`);
    setModalEditNd(null);
    if (vacEditId === id) descartarEdicionVacaciones();
    cargarNoDisponible();
  }

  async function guardarEditNd() {
    try {
      await api.put(`/agenda-no-disponible/${editNdForm.id}`, ndFormToApi(editNdForm));
      setModalEditNd(null);
      setEditNdForm({});
      cargarNoDisponible();
    } catch (e) { console.error(e); }
  }

  function abrirEditNd(nd) {
    if (nd.tipo === "guardia_sabado") {
      setModalGuardiasSabados(true);
      return;
    }
    if (nd.tipo === "horario_base") {
      void dialogs.alert(
        "Este bloque viene del horario habitual del agente. Edítalo en Configuración → Agentes."
      );
      return;
    }
    setEditNdForm(ndFormFromApi(nd));
    setModalEditNd(nd);
  }

  function abrirModalNoDisp() {
    const hoy = fechaISO(new Date());
    const agenteId = usuario?.agente_id ? String(usuario.agente_id) : "";
    setNoDispForm(ndFormFromApi({
      agente_id: agenteId,
      tipo: "otro",
      motivo: "",
      fecha_inicio: hoy,
      fecha_fin: hoy,
      todo_el_dia: true,
    }));
    setModalNoDisp(true);
  }

  function ndTitle(nd) {
    const base = `${nd.agente_nombre}: ${nd.motivo}`;
    if (nd.tipo === "guardia_sabado") {
      return `${base} — clic para ver guardias`;
    }
    if (nd.tipo === "horario_base") {
      return `${base} — horario habitual (Configuración → Agentes)`;
    }
    return `${base} — clic para editar`;
  }

  /** Tooltip de los cuadrados con inicial en cabecera semana (texto corto). */
  function tituloCuadradoNd(nd, dia) {
    const nombre = nd.agente_nombre || "Agente";
    if (nd.tipo === "guardia_sabado") {
      return `${nombre} no trabaja el sábado - clic para ver guardias`;
    }
    if (nd.tipo === "horario_base") {
      return `${nombre} — fuera de horario habitual`;
    }
    const diasL = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
    return `${nombre} no trabaja el ${diasL[dia.getDay()]} - clic para editar`;
  }

  function contenidoBloqueNd(nd, color, altoPx) {
    const motivo = formatNdMotivoCorto(nd);
    const compacto = altoPx < 52;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          padding: "2px 4px",
          maxWidth: "100%",
          pointerEvents: "none",
        }}
      >
        <span
          style={{
            fontSize: compacto ? 8 : 10,
            fontWeight: 700,
            color,
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
          }}
        >
          {inicialNombreAgente(nd.agente_nombre)}
        </span>
        {altoPx >= 34 && (
          <span
            style={{
              fontSize: compacto ? 9 : 10,
              fontWeight: 500,
              color,
              opacity: 0.92,
              textAlign: "center",
              lineHeight: 1.15,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: compacto ? 2 : 4,
              WebkitBoxOrient: "vertical",
              wordBreak: "break-word",
            }}
          >
            {motivo}
          </span>
        )}
      </div>
    );
  }

  function renderBloqueNdSemana(nd, { topPx, altoPx, col, colIdx, lineaGrilla, ALTO_HORA }) {
    const { borde, fondo, color } = getEstiloNdAgenda(nd.agente_id, isDark);
    return (
      <div
        key={nd.id}
        onClick={(e) => { e.stopPropagation(); abrirEditNd(nd); }}
        title={ndTitle(nd)}
        style={{
          position: "absolute",
          top: topPx,
          left: col.left,
          width: col.width,
          height: altoPx,
          background: fondo,
          borderLeft: colIdx === 0 ? `3px solid ${borde}` : `2px solid ${lineaGrilla}`,
          boxSizing: "border-box",
          zIndex: 3,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {contenidoBloqueNd(nd, color, altoPx)}
      </div>
    );
  }

  function abrirModalCita(cita) {
    if (esCitaVisita(cita)) {
      abrirVisitaAgenda(cita);
      return;
    }
    setCitaForm(mapCitaToFormConUsuario(cita));
    setModalCita(cita);
  }

  function renderBloqueCita(cita, columnaEl, diaKey, diaDate, overlapLayout) {
    const c = citaConPreview(cita);
    const { topPx, altoPx } = posicionBloqueCita(c);
    const estilo = estiloBloqueCita(c);
    const esActiva = activaId === cita.id;
    const caja = attrsCajaCita(c, estilo, { arrastre: esActiva });
    const slot = overlapLayout?.get?.(cita.id) || { column: 0, columnCount: 1 };
    return (
      <BloqueCitaCalendario
        key={cita.id}
        cita={c}
        topPx={topPx}
        altoPx={altoPx}
        estilo={estilo}
        caja={caja}
        esActiva={esActiva}
        overlapColumn={slot.column}
        overlapColumnCount={slot.columnCount}
        onMoverStart={puedeModificarCampoFechaHora ? (e) => iniciarMover(e, cita, columnaEl, diaKey, diaDate) : undefined}
        onResizeStart={puedeModificarCampoFechaHora ? (e) => iniciarResize(e, cita, columnaEl, diaKey, diaDate) : undefined}
        onAbrir={abrirModalCita}
        huboMovimientoRef={huboMovimientoRef}
        horaStr={horaStr}
        nombreContacto={nombreContactoCita}
        telefonoContacto={telefonoContactoCita}
      />
    );
  }


  function VistaDia() {
    const horas = Array.from({ length: 11 }, (_, i) => i + HORA_INICIO_AGENDA);
    const diaStr = fechaISO(fechaActual);
    const citasDia = citasFiltradas().filter((ct) => fechaLocalDeIso(ct.fecha_inicio) === diaStr);
    const overlapLayout = computeAgendaOverlapLayout(citasDia);
    const esDomingo = fechaActual.getDay() === 0;
    const festDia = esFestivo(diaStr) || esDomingo;
    const lineaGrilla = isDark ? "#64748b" : "#9ca398";
    const tituloDiaRojo = esFestivo(diaStr)
      ? (festivos.find((f) => f.fecha === diaStr && f.activo)?.nombre || "Festivo")
      : esDomingo ? "Domingo" : null;
    const ALTO_HORA = ALTO_HORA_AGENDA_PX;

    return (
      <div style={{ overflowY: "auto", flex: 1, background: festDia ? getDayCellBg(fechaActual) : (isDark ? "transparent" : "#ffffff") }}>
        {tituloDiaRojo && (
          <div style={{ padding: "8px 16px", fontSize: 12, fontWeight: 600, color: "#ffffff", borderBottom: "1px solid rgba(0,0,0,0.2)" }}>
            {tituloDiaRojo}
          </div>
        )}
        <div style={{ display: "flex", minWidth: 320 }}>
          <div style={{ width: 60, flexShrink: 0 }}>
            {horas.map((h) => (
              <div
                key={h}
                style={{
                  height: ALTO_HORA,
                  borderBottom: `1px solid ${lineaGrilla}`,
                  borderRight: `2px solid ${lineaGrilla}`,
                  padding: "4px 8px 0 0",
                  fontSize: 11,
                  color: festDia ? "rgba(255,255,255,0.92)" : c.textSecondary,
                  textAlign: "right",
                  background: isDark ? "transparent" : "#e8e8e0",
                }}
              >
                {pad(h)}:00
              </div>
            ))}
          </div>
          <div
            ref={(el) => {
              columnaDiaRef.current = el;
              registrarColumna(diaStr, el, fechaActual);
            }}
            style={{
              flex: 1,
              position: "relative",
              height: ALTO_HORA * horas.length,
              background: getDayCellBg(fechaActual),
            }}
          >
            {horas.map((h) => (
              <div
                key={h}
                onClick={() => {
                  abrirNuevaCitaAgenda({
                    fecha_inicio: `${diaStr}T${pad(h)}:00`,
                    fecha_fin: `${diaStr}T${pad(h + 1)}:00`,
                    duracion_minutos: 60,
                  });
                }}
                style={{
                  position: "absolute",
                  top: (h - HORA_INICIO_AGENDA) * ALTO_HORA,
                  left: 0,
                  right: 0,
                  height: ALTO_HORA,
                  borderBottom: `1px solid ${lineaGrilla}`,
                  cursor: "pointer",
                  zIndex: 1,
                }}
              />
            ))}
            {citasDia.map((cita) => renderBloqueCita(cita, columnaDiaRef.current, diaStr, fechaActual, overlapLayout))}
          </div>
        </div>
      </div>
    );
  }

  function VistaSemana() {
    const dow = (fechaActual.getDay() + 6) % 7;
    const lunes = new Date(fechaActual); lunes.setDate(fechaActual.getDate() - dow);
    const dias = Array.from({ length: 7 }, (_, i) => { const d = new Date(lunes); d.setDate(lunes.getDate() + i); return d; });
    const horas = Array.from({ length: 11 }, (_, i) => i + 9);
    const ALTO_HORA = 60;
    const lineaGrilla = isDark ? "#64748b" : "#9ca398";

    return (
      <div style={{ overflowX: "auto", overflowY: "auto", flex: 1 }}>
        <div style={{ minWidth: 700 }}>

          {/* HEADER DÍAS */}
          <div style={{ display: "flex", borderBottom: `2px solid ${lineaGrilla}`, position: "sticky", top: 0, background: isDark ? c.bgPanel : '#e8e8e0', zIndex: 2 }}>
            <div style={{ width: 60, flexShrink: 0, borderRight: `2px solid ${lineaGrilla}` }} />
            {dias.map((dia, i) => {
              const esHoy = fechaISO(dia) === fechaISO(new Date());
              const diaStr2 = fechaISO(dia);
              const ndDia = noDisponible.filter(nd => {
                const fiStr = nd.fecha_inicio?.slice(0,10);
                const ffStr = nd.fecha_fin?.slice(0,10);
                return diaStr2 >= fiStr && diaStr2 <= ffStr &&
                  ndVisibleParaSeleccion(nd);
              });
              const ndDiaTodo = dedupeNdPorAgente(ndDia.filter((nd) => ndTodoElDia(nd.todo_el_dia)));
              const esFest = esFestivo(diaStr2);
              const rojoCab = esFest || dia.getDay() === 0;
              return (
                <div key={i} style={{ flex: 1, textAlign: "center", padding: "8px 4px 4px", borderLeft: `1px solid ${lineaGrilla}`, background: getDayHeaderBg(dia) }}>
                  <div style={{ fontSize: 11, color: rojoCab ? "#ffffff" : c.textSecondary }}>{DIAS_SEMANA[i]}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: esHoy ? c.accentBlue : (rojoCab ? "#ffffff" : c.textPrimary), width: 28, height: 28, borderRadius: "50%", background: esHoy ? (isDark ? mezclarColor("#2563eb", "#0f172a", 0.48) : mezclarColor("#2563eb", "#ffffff", 0.82)) : "transparent", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}>{dia.getDate()}</div>
                  {esFest && <div style={{ fontSize: 9, color: "rgba(255,255,255,0.95)", marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 2px' }}>{festivos.find(f => f.fecha === diaStr2 && f.activo)?.nombre}</div>}
                  {!rojoCab && ndDiaTodo.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "center", alignItems: "center", marginTop: 6, padding: "0 2px" }}>
                      {ndDiaTodo.map(nd => {
                        const bg = getColorAgente(nd.agente_id);
                        return (
                          <div
                            key={nd.id}
                            onClick={(e) => { e.stopPropagation(); abrirEditNd(nd); }}
                            title={tituloCuadradoNd(nd, dia)}
                            style={{
                              width: 22,
                              height: 20,
                              borderRadius: 4,
                              background: bg,
                              color: textoSobreColorHex(bg),
                              fontSize: 9,
                              fontWeight: 700,
                              letterSpacing: "-0.04em",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              flexShrink: 0,
                              boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.45)" : "0 1px 2px rgba(0,0,0,0.2)",
                              border: "1px solid rgba(0,0,0,0.15)",
                            }}
                          >
                            {inicialNombreAgente(nd.agente_nombre)}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {dia.getDay() >= 1 && dia.getDay() <= 5 && guardiasDiaPorFecha[diaStr2] && (
                    <div
                      title={`Día de guardia: ${guardiasDiaPorFecha[diaStr2]}`}
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        marginTop: 6,
                        padding: "0 4px",
                        lineHeight: 1.25,
                        color: rojoCab ? "rgba(255,255,255,0.95)" : (isDark ? "#5eead4" : "#0f766e"),
                      }}
                    >
                      📌 {(guardiasDiaPorFecha[diaStr2] || "").split(" ")[0]}
                    </div>
                  )}
                  {dia.getDay() === 6 && guardiasSabadoPorFecha[diaStr2] && (
                    <div
                      title={`Guardia sábado: ${guardiasSabadoPorFecha[diaStr2].titulo}`}
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        marginTop: 6,
                        padding: "0 4px",
                        lineHeight: 1.25,
                        color: rojoCab ? "rgba(255,255,255,0.95)" : (isDark ? "#5eead4" : "#0f766e"),
                      }}
                    >
                      📌 {guardiasSabadoPorFecha[diaStr2].iniciales}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* GRID DE HORAS */}
          <div style={{ display: "flex" }}>
            {/* Columna horas */}
            <div style={{ width: 60, flexShrink: 0 }}>
              {horas.map(h => (
                <div key={h} style={{ height: ALTO_HORA, borderBottom: `1px solid ${lineaGrilla}`, borderRight: `2px solid ${lineaGrilla}`, padding: "4px 8px 0 0", fontSize: 11, color: c.textSecondary, textAlign: "right", background: isDark ? 'transparent' : '#e8e8e0' }}>{pad(h)}:00</div>
              ))}
            </div>

            {/* Columnas días */}
            {dias.map((dia, i) => {
              const diaStr = fechaISO(dia);
              const esDiaCerrado = esDiaNoLaborable(dia);
              const citasDia = citasFiltradas().filter(ct => fechaLocalDeIso(ct.fecha_inicio) === diaStr);
              const overlapLayout = computeAgendaOverlapLayout(citasDia);

              const ndTodoDia = dedupeNdPorAgente(noDisponible.filter(nd => {
                const fiStr = nd.fecha_inicio?.slice(0,10);
                const ffStr = nd.fecha_fin?.slice(0,10);
                const esTodoDia = nd.todo_el_dia === true || nd.todo_el_dia === 't' || nd.todo_el_dia === 'true';
                return !esDiaCerrado && esTodoDia && diaStr >= fiStr && diaStr <= ffStr &&
                  ndVisibleParaSeleccion(nd);
              }));

              const ndParciales = noDisponible.filter(nd => {
                const fiStr = nd.fecha_inicio?.slice(0,10);
                const ffStr = nd.fecha_fin?.slice(0,10);
                const esTodoDia = nd.todo_el_dia === true || nd.todo_el_dia === 't' || nd.todo_el_dia === 'true';
                return !esDiaCerrado && !esTodoDia && nd.hora_inicio && diaStr >= fiStr && diaStr <= ffStr &&
                  ndVisibleParaSeleccion(nd);
              });

              const ndTodoSorted = [...ndTodoDia].sort((a, b) => {
                const na = String(a.agente_nombre || "").localeCompare(String(b.agente_nombre || ""));
                if (na !== 0) return na;
                return String(a.id).localeCompare(String(b.id));
              });

              const ndParcialList = ndParcialesParaColumnas(ndParciales);
              const columnasNd = buildAgentesColumnasIndex(ndTodoSorted, ndParcialList);
              const ndCols = columnasNd.count || 1;

              function estiloColumnaNd(agenteId) {
                const col = columnasNd.colMap.get(String(agenteId)) ?? 0;
                const w = 100 / ndCols;
                return {
                  left: `${col * w}%`,
                  width: `${w}%`,
                };
              }

              const franjaOficina = ndFranjaTodoElDiaSemanaPx(ALTO_HORA);

              return (
                <div
                  key={i}
                  ref={(el) => registrarColumna(diaStr, el, dia)}
                  style={{ flex: 1, borderLeft: `1px solid ${lineaGrilla}`, position: "relative", height: ALTO_HORA * horas.length, background: getDayCellBgSemanaGrid(dia) }}>

                  {esDiaCerrado && (
                    <div
                      aria-hidden
                      style={{
                        position: "absolute",
                        top: franjaOficina.topPx,
                        left: 0,
                        right: 0,
                        height: franjaOficina.altoPx,
                        background: COLOR_BOTON_FESTIVOS,
                        zIndex: 0,
                        pointerEvents: "none",
                      }}
                    />
                  )}

                  {/* Bloques no disponibilidad (todo el día y parcial): mismo aspecto sólido + iniciales y motivo */}
                  {ndTodoSorted.map((nd) => {
                    const col = estiloColumnaNd(nd.agente_id);
                    const colIdx = columnasNd.colMap.get(String(nd.agente_id)) ?? 0;
                    const { topPx, altoPx } = ndFranjaTodoElDiaSemanaPx(ALTO_HORA);
                    return renderBloqueNdSemana(nd, {
                      topPx,
                      altoPx,
                      col,
                      colIdx,
                      lineaGrilla,
                      ALTO_HORA,
                    });
                  })}

                  {/* Líneas de hora de fondo */}
                  {horas.map(h => (
                    <div key={h} onClick={() => abrirNuevaCitaAgenda({ fecha_inicio: `${diaStr}T${pad(h)}:00`, fecha_fin: `${diaStr}T${pad(h+1)}:00`, duracion_minutos: 60 })}
                      style={{ position: "absolute", top: (h - 9) * ALTO_HORA, left: 0, right: 0, height: ALTO_HORA, borderBottom: `1px solid ${lineaGrilla}`, cursor: "pointer", zIndex: 1 }} />
                  ))}

                  {ndParcialList.map((nd) => {
                    const { topPx, altoPx } = ndFranjaSemanaPx(nd.hora_inicio, nd.hora_fin, ALTO_HORA);
                    const col = estiloColumnaNd(nd.agente_id);
                    const colIdx = columnasNd.colMap.get(String(nd.agente_id)) ?? 0;
                    return renderBloqueNdSemana(nd, {
                      topPx,
                      altoPx,
                      col,
                      colIdx,
                      lineaGrilla,
                      ALTO_HORA,
                    });
                  })}

                  {/* Bloques citas */}
                  {citasDia.map((cita) => renderBloqueCita(cita, getColumnaEl(diaStr), diaStr, dia, overlapLayout))}

                </div>
              );
            })}
          </div>

        </div>
      </div>
    );
  }

  function VistaMes() {
    const año = fechaActual.getFullYear();
    const mes = fechaActual.getMonth();
    const primerDia = new Date(año, mes, 1);
    const ultimoDia = new Date(año, mes + 1, 0);
    const inicioCal = new Date(primerDia); inicioCal.setDate(primerDia.getDate() - ((primerDia.getDay() + 6) % 7));
    const dias = [];
    const cur = new Date(inicioCal);
    while (cur <= ultimoDia || dias.length % 7 !== 0) {
      dias.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
      if (dias.length > 42) break;
    }
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: `3px solid ${isDark ? c.sectionBorder : '#b8b8ae'}`, background: isDark ? c.bgPanel : '#e8e8e0' }}>
          {DIAS_SEMANA.map(d => <div key={d} style={{ textAlign: "center", padding: "6px 0", fontSize: 12, fontWeight: 600, color: c.textSecondary }}>{d}</div>)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", flex: 1 }}>
          {dias.map((dia, i) => {
            const esMesActual = dia.getMonth() === mes;
            const esHoy = fechaISO(dia) === fechaISO(new Date());
            const diaStr = fechaISO(dia);
            const citasDia = citasFiltradas().filter(ct => fechaLocalDeIso(ct.fecha_inicio) === diaStr);
            const ndDia = dedupeNdPorAgente(noDisponible.filter(nd => {
              const fiStr = nd.fecha_inicio?.slice(0,10);
              const ffStr = nd.fecha_fin?.slice(0,10);
              return diaStr >= fiStr && diaStr <= ffStr &&
                ndVisibleParaSeleccion(nd);
            }));
            const esFestMes = esFestivo(diaStr);
            const esDomMes = dia.getDay() === 0;
            const rojoMes = esFestMes || esDomMes;
            const celdaBg = rojoMes ? getDayCellBg(dia) : esHoy ? (isDark ? mezclarColor(c.accentBlue, "#0f172a", 0.55) : "#e8f0f8") : getDayCellBg(dia);
            return (
              <div key={i} onClick={() => { setVista("dia"); setFechaActual(new Date(dia)); }}
                style={{ borderRight: `1px solid ${isDark ? c.sectionBorder : '#c8c8be'}`, borderBottom: `1px solid ${isDark ? c.sectionBorder : '#c8c8be'}`, padding: "6px 4px", minHeight: 90, cursor: "pointer", opacity: esMesActual ? 1 : 0.5, background: celdaBg }}>
                <div style={{ fontSize: 13, fontWeight: esHoy ? 700 : 400, color: esHoy ? c.accentBlue : (rojoMes ? "#ffffff" : c.textPrimary), marginBottom: 2 }}>{dia.getDate()}</div>
                {esFestMes && <div style={{ fontSize: 8.5, color: "rgba(255,255,255,0.95)", marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{festivos.find(f => f.fecha === diaStr && f.activo)?.nombre}</div>}
                {!rojoMes && ndDia.slice(0, 1).map(nd => (
                  <div key={nd.id} onClick={e => { e.stopPropagation(); abrirEditNd(nd); }}
                    style={{ fontSize: 10, background: mezclarColor("#ef4444", isDark ? "#0f172a" : "#ffffff", isDark ? 0.5 : 0.15), color: isDark ? "#fecaca" : "#991b1b", borderRadius: 3, padding: "1px 3px", marginBottom: 2, cursor: "pointer", border: isDark ? "none" : "1px solid #fecaca" }}>
                    🚫 {nd.motivo}
                  </div>
                ))}
                {citasDia.slice(0,3).map(cita => {
                  const estiloM = estiloBloqueCita(cita);
                  const cajaM = attrsCajaCita(cita, estiloM);
                  return (
                  <div key={cita.id} className={cajaM.className} data-tipo={cajaM["data-tipo"]} onClick={e => { e.stopPropagation(); abrirModalCita(cita); }}
                    style={{ fontSize: 10, padding: "1px 4px", marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", ...cajaM.style }}>
                    {(() => {
                      const h = horaStr(cita.fecha_inicio);
                      const nom = nombreContactoCita(cita) || cita.titulo || "";
                      const l1 = linea1BloqueCita(cita, { hora: h, nombre: nom, telefono: telefonoContactoCita(cita) });
                      const l2 = linea2BloqueCita(cita);
                      return l2 ? `${l1} — ${l2}` : l1;
                    })()}
                  </div>
                  );
                })}
                {citasDia.length > 3 && <div style={{ fontSize: 10, color: c.textSecondary }}>+{citasDia.length - 3} más</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const inputStyle = { width: "100%", background: c.bgInput, border: `1px solid ${c.sectionBorder}`, borderRadius: 8, padding: "8px 12px", color: c.textPrimary, fontSize: 14, boxSizing: "border-box" };

  return (
    <div style={{ height: "calc(100vh - 56px)", display: "flex", flexDirection: "column", background: isDark ? c.bgApp : '#eeeee8', overflow: "hidden" }}>

      {/* HEADER */}
      <div style={{ padding: "12px 20px", borderBottom: `2px solid ${isDark ? c.sectionBorder : '#c8c8be'}`, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", background: isDark ? c.bgPanel : '#f4f4ee' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: c.textPrimary, marginRight: 8 }}>Agenda</div>
        {puedeVerNavHoy && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={() => navegar(-1)} style={{ background: c.bgInput, border: `1px solid ${c.sectionBorder}`, color: c.textPrimary, borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 16 }}>‹</button>
            <button onClick={() => setFechaActual(new Date())} style={{ background: c.bgInput, border: `1px solid ${c.sectionBorder}`, color: c.textPrimary, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12 }}>Hoy</button>
            <button onClick={() => navegar(1)} style={{ background: c.bgInput, border: `1px solid ${c.sectionBorder}`, color: c.textPrimary, borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 16 }}>›</button>
            <span style={{ fontSize: 14, fontWeight: 600, color: c.textPrimary, minWidth: 200 }}>{getTituloNavegacion()}</span>
          </div>
        )}
        {(puedeVerVistaDia || puedeVerVistaSemana || puedeVerVistaMes) && (
          <div style={{ display: "flex", gap: 0, border: `1px solid ${c.sectionBorder}`, borderRadius: 8, overflow: "hidden" }}>
            {["dia","semana","mes"].filter(v => (v === "dia" ? puedeVerVistaDia : v === "semana" ? puedeVerVistaSemana : puedeVerVistaMes)).map(v => (
              <button key={v} onClick={() => setVista(v)} style={{ padding: "6px 14px", background: vista === v ? "#0369a1" : c.bgInput, color: vista === v ? "#fff" : c.textSecondary, border: "none", cursor: "pointer", fontSize: 13, fontWeight: vista === v ? 600 : 400 }}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        )}
        {puedeVerFiltroUsuario && (
          <select value={usuarioSeleccionado} onChange={e => setUsuarioSeleccionado(e.target.value)} style={{ background: c.bgInput, border: `1px solid ${c.sectionBorder}`, borderRadius: 8, padding: "6px 12px", color: c.textPrimary, fontSize: 13 }}>
            <option value="todos">Todos los usuarios</option>
            {usuariosAgenda.map((u) => (
              <option key={u.id} value={u.id}>{u.nombre}{u.agente_es_ia ? " ⚡" : ""}</option>
            ))}
          </select>
        )}
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={{ background: c.bgInput, border: `1px solid ${c.sectionBorder}`, borderRadius: 8, padding: "6px 12px", color: c.textPrimary, fontSize: 13 }}>
          <option value="todos">Todos los tipos</option>
          {TIPOS.filter(puedeVerTipoCita).map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {puedeVerGuardias && (
            <button onClick={() => setModalGuardiasSabados(true)} style={{ background: "#0369a1", border: "1px solid #0369a1", color: "#ffffff", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>📋 Guardias sábados</button>
          )}
          {puedeVerGuardiaDia && (
            <button onClick={() => setModalGuardiaDia(true)} style={{ background: "#0f766e", border: "1px solid #0f766e", color: "#ffffff", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>📌 Día de guardia</button>
          )}
          {puedeVerVacaciones && (
            <button onClick={() => { const now = new Date(); setCalYear(now.getFullYear()); setCalMonth(now.getMonth()); setHoveredDate(null); setVacEditId(null); setVacForm({ fecha_inicio: '', fecha_fin: '', motivo: 'Vacaciones' }); setVacAgenteIds([]); setModalVacaciones(true); }} style={{ background: "#d97706", border: "1px solid #d97706", color: "#ffffff", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>🏖️ Vacaciones</button>
          )}
          {puedeVerFestivos && (
            <button onClick={() => { setFestivosYear(new Date().getFullYear()); setEditFestivo(null); setModalFestivos(true); }} style={{ background: COLOR_BOTON_FESTIVOS, border: `1px solid ${COLOR_BOTON_FESTIVOS}`, color: "#ffffff", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>📅 Festivos</button>
          )}
          {puedeVerNoDisp && (
            <button onClick={abrirModalNoDisp} style={{ background: isDark ? "#6b728022" : "#f3f4f6", border: `1px solid #6b7280`, color: isDark ? "#9ca3af" : "#374151", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>🚫 No disponible</button>
          )}
          {puedeVerNuevaCita && (
            <button onClick={() => abrirNuevaCitaAgenda({})} style={{ background: "linear-gradient(135deg, #0369a1, #0ea5e9)", color: "#fff", border: "none", borderRadius: 8, padding: "6px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>+ Nueva cita</button>
          )}
        </div>
      </div>

      {/* LEYENDA */}
      <div style={{ padding: "8px 20px", borderBottom: `2px solid ${isDark ? c.sectionBorder : '#c8c8be'}`, display: "flex", gap: 16, flexWrap: "wrap", background: isDark ? c.bgPanel : '#f4f4ee' }}>
        {verTodosUsuarios
          ? usuariosAgenda.map((u) => (
              <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", background: getColorUsuarioAgenda(u), flexShrink: 0, boxShadow: "0 0 0 1px rgba(0,0,0,0.15)" }} />
                <span style={{ fontSize: 12, color: c.textSecondary }}>{u.nombre}{u.agente_es_ia ? " ⚡" : ""}</span>
              </div>
            ))
          : TIPOS.map((t) => {
              const paleta = getColoresTipoCitaAgenda(isDark);
              const cfg = paleta[t] || paleta.otro;
              const ley = propsLeyendaTipo(t, isDark);
              return (
                <div key={t} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div className={ley.className} data-tipo={ley["data-tipo"]} style={{ width: 12, height: 12, borderRadius: "50%", flexShrink: 0, ...ley.style }} />
                  <span style={{ fontSize: 12, color: c.textSecondary }}>{cfg.etiqueta}</span>
                </div>
              );
            })}
      </div>

      {/* CONTENIDO */}
      {loading ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: c.textSecondary }}>Cargando...</div>
      ) : (
        <>
          {vista === "dia" && <VistaDia />}
          {vista === "semana" && <VistaSemana />}
          {vista === "mes" && <VistaMes />}
        </>
      )}

      {/* MODAL CITA — dos columnas, sin scroll; calendario + columna de horas como la agenda */}
      {(modalCita === "nueva" || (modalCita && modalCita.id)) && !esCitaVisita(citaForm?.tipo ? citaForm : modalCita) && (() => {
        const inp = { width: "100%", background: c.bgInput, border: `1px solid ${c.sectionBorder}`, borderRadius: 8, padding: "6px 10px", color: c.textPrimary, fontSize: 13, boxSizing: "border-box" };
        const usuarioMiId = usuario?.id ? String(usuario.id) : "";
        const usuarioExplicitVacio = citaForm.usuario_id === "";
        const usuarioSelectValue = usuarioExplicitVacio
          ? ""
          : (citaForm.usuario_id ? String(citaForm.usuario_id) : (modalCita === "nueva" && usuarioMiId ? usuarioMiId : ""));
        const usuarioEnLista = usuarioMiId && usuariosAgenda.some((u) => String(u.id) === usuarioMiId);
        const fkSel = citaTcTarget === "inicio" ? "fecha_inicio" : "fecha_fin";
        const curSel = citaForm[fkSel] || "";
        let selH = 9;
        let selM = 0;
        if (curSel.includes("T")) {
          const pr = curSel.split("T")[1].split(":");
          selH = parseInt(pr[0], 10);
          selM = parseInt(pr[1], 10);
          if (!Number.isFinite(selH)) selH = 9;
          if (!Number.isFinite(selM)) selM = 0;
        }
        return (
          <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }} onClick={() => setModalCita(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: c.bgPanel, borderRadius: 14, padding: "18px 22px", width: 1000, maxWidth: "calc(100vw - 24px)", overflow: "hidden", boxShadow: "0 8px 40px #00000066", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexShrink: 0 }}>
                <h3 style={{ color: c.textPrimary, margin: 0, fontSize: 17 }}>{modalCita === "nueva" ? "Nueva cita" : "Editar cita"}</h3>
                <button type="button" onClick={() => setModalCita(null)} style={{ background: "none", border: "none", color: c.textSecondary, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
              </div>
              {modalCita !== "nueva" && modalCita.creado_por_ia && (
                <div style={{ background: "#0891B222", border: "1px solid #0891B244", borderRadius: 8, padding: "6px 10px", marginBottom: 10, fontSize: 11, color: "#0891B2", flexShrink: 0 }}>⚡ Esta cita fue creada por Ana</div>
              )}
              <div style={{ display: "flex", gap: 22, alignItems: "stretch", flexShrink: 0 }}>
                {/* Izquierda: datos */}
                <div style={{ flex: "0 0 300px", display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
                  {puedeVerCampoTitulo && (
                  <div>
                    <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 3, fontWeight: 600 }}>Título</label>
                    <input
                      value={citaForm.titulo || ""}
                      onChange={e => setCitaForm(p => ({ ...p, titulo: e.target.value }))}
                      placeholder="Opcional"
                      disabled={!puedeModificarCampoTitulo}
                      title={!puedeModificarCampoTitulo ? "No tienes permiso para modificar el título" : undefined}
                      style={{ ...inp, opacity: puedeModificarCampoTitulo ? 1 : 0.6, cursor: puedeModificarCampoTitulo ? "text" : "not-allowed" }}
                    />
                  </div>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 3, fontWeight: 600 }}>Tipo</label>
                      <select
                        value={citaForm.tipo || "captacion"}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "visita") {
                            abrirNuevaVisitaAgenda({
                              ...citaForm,
                              fecha_inicio: citaForm.fecha_inicio,
                              fecha_fin: citaForm.fecha_fin,
                              duracion_minutos: citaForm.duracion_minutos || 60,
                            });
                            setModalCita(null);
                            return;
                          }
                          setCitaForm((p) => ({ ...p, tipo: v }));
                        }}
                        style={inp}
                      >
                        {TIPOS.filter(puedeVerTipoCita).map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                      </select>
                    </div>
                    {puedeVerCampoEstado && (
                    <div>
                      <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 3, fontWeight: 600 }}>Estado</label>
                      <select
                        value={citaForm.estado || "pendiente"}
                        onChange={e => setCitaForm(p => ({ ...p, estado: e.target.value }))}
                        disabled={!puedeModificarCampoEstado}
                        title={!puedeModificarCampoEstado ? "No tienes permiso para modificar el estado" : undefined}
                        style={{ ...inp, opacity: puedeModificarCampoEstado ? 1 : 0.6, cursor: puedeModificarCampoEstado ? "pointer" : "not-allowed" }}
                      >
                        {["pendiente", "confirmada", "cancelada", "realizada"].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                      </select>
                    </div>
                    )}
                  </div>
                  {puedeVerCampoUsuario && (
                  <div>
                    <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 3, fontWeight: 600 }}>Usuario (cuenta CRM)</label>
                    <select
                      value={usuarioSelectValue}
                      onChange={(e) => {
                        const uid = e.target.value;
                        const agenteDelUsuario = resolverAgenteParaUsuarioId(uid);
                        setCitaForm((p) => ({
                          ...p,
                          usuario_id: uid,
                          agente_id: agenteDelUsuario || "",
                        }));
                      }}
                      disabled={!puedeModificarCampoUsuario}
                      title={!puedeModificarCampoUsuario ? "No tienes permiso para modificar el usuario asignado" : undefined}
                      style={{ ...inp, opacity: puedeModificarCampoUsuario ? 1 : 0.6, cursor: puedeModificarCampoUsuario ? "pointer" : "not-allowed" }}
                    >
                      <option value="">Sin asignar</option>
                      {usuarioMiId && !usuarioEnLista && (
                        <option value={usuarioMiId}>
                          {usuario?.nombre || "Usuario"}
                          {usuario?.agente_nombre ? ` — agente ${usuario.agente_nombre}` : ""}
                        </option>
                      )}
                      {usuariosAgenda.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.nombre}
                          {u.agente_nombre && u.agente_nombre !== u.nombre ? ` — ${u.agente_nombre}` : ""}
                          {u.agente_es_ia ? " ⚡" : ""}
                        </option>
                      ))}
                    </select>
                    {(citaForm.agente_nombre || (modalCita && modalCita !== "nueva" && modalCita.agente_nombre)) && (
                      <p style={{ margin: "4px 0 0", fontSize: 10, color: c.textSecondary }}>
                        Agente comercial: {citaForm.agente_nombre || modalCita.agente_nombre}
                      </p>
                    )}
                    {!usuarioSelectValue && modalCita !== "nueva" && (
                      <p style={{ margin: "4px 0 0", fontSize: 10, color: "#f59e0b" }}>
                        Sin usuario asignado: elige quién gestiona la cita en el calendario (no es lo mismo que el agente del cliente).
                      </p>
                    )}
                  </div>
                  )}
                  {puedeVerCampoContacto && (
                  <>
                  <div style={{ position: "relative" }}>
                    <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 3, fontWeight: 600 }}>Contacto <span style={{ fontWeight: 500, opacity: 0.75 }}>(opcional)</span></label>
                    <input
                      value={citaContactoBuscar}
                      onChange={(e) => setCitaContactoBuscar(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        const txt = String(citaContactoBuscar || "").trim();
                        if (!txt || !puedeModificarCampoContacto) return;
                        // Texto libre: sin ficha CRM (Enter sin elegir de la lista)
                        setCitaForm((p) => ({
                          ...p,
                          cliente_id: null,
                          contacto_id: null,
                          cliente_nombre: null,
                          cliente_apellido: null,
                          cliente_telefono: null,
                          contacto_nombre: null,
                          contacto_apellidos: null,
                          contacto_tipo: null,
                          contacto_telefono: null,
                          telefono_contacto: p.telefono_contacto || "",
                          titulo: p.titulo?.trim() ? p.titulo : txt,
                          cliente_texto: txt,
                        }));
                        setCitaContactoBuscar("");
                        setCitaContactoHits([]);
                      }}
                      placeholder="Buscar en CRM o escribe un nombre y pulsa Enter"
                      disabled={!puedeModificarCampoContacto}
                      title={!puedeModificarCampoContacto ? "No tienes permiso para modificar el contacto" : undefined}
                      style={{ ...inp, opacity: puedeModificarCampoContacto ? 1 : 0.6, cursor: puedeModificarCampoContacto ? "text" : "not-allowed" }}
                      autoComplete="off"
                    />
                    {citaContactoHits.length > 0 && (
                      <div style={{ position: "absolute", zIndex: 30, left: 0, right: 0, top: "100%", marginTop: 2, maxHeight: 200, overflowY: "auto", border: `1px solid ${c.sectionBorder}`, borderRadius: 8, background: c.bgPanel, boxShadow: "0 8px 24px rgba(0,0,0,0.18)" }}>
                        {citaContactoHits.map((row) => (
                          <button
                            key={`${row.kind}-${row.id}`}
                            type="button"
                            onClick={() => {
                              if (row.kind === "cliente") {
                                setCitaForm((p) => ({
                                  ...p,
                                  cliente_id: row.id,
                                  cliente_nombre: row.label.split(" ")[0] || row.label,
                                  cliente_apellido: row.apellido || row.label.split(" ").slice(1).join(" ") || null,
                                  cliente_telefono: row.telefono || null,
                                  telefono_contacto: row.telefono || "",
                                  contacto_id: null,
                                  contacto_nombre: null,
                                  contacto_apellidos: null,
                                  contacto_tipo: null,
                                  contacto_telefono: null,
                                  cliente_texto: null,
                                }));
                              } else {
                                setCitaForm((p) => ({
                                  ...p,
                                  contacto_id: row.id,
                                  cliente_id: null,
                                  cliente_nombre: null,
                                  cliente_apellido: null,
                                  cliente_telefono: null,
                                  contacto_nombre: row.nom || row.label,
                                  contacto_apellidos: row.apellidos || null,
                                  contacto_tipo: row.tipoRaw,
                                  contacto_telefono: row.telefono || null,
                                  telefono_contacto: row.telefono || "",
                                  cliente_texto: null,
                                }));
                              }
                              setCitaContactoBuscar("");
                              setCitaContactoHits([]);
                            }}
                            style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", border: "none", borderBottom: `1px solid ${c.sectionBorder}`, background: "transparent", cursor: "pointer", color: c.textPrimary }}
                          >
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{row.label}</div>
                            <div style={{ fontSize: 10, color: c.textMuted }}>{row.tag}{row.sub ? ` · ${row.sub}` : ""}</div>
                          </button>
                        ))}
                      </div>
                    )}
                    {citaContactoCargando && <div style={{ fontSize: 10, color: c.textMuted, marginTop: 4 }}>Buscando…</div>}
                    {(citaForm.cliente_id || citaForm.contacto_id || citaForm.cliente_texto) && (
                      <div style={{ marginTop: 6, fontSize: 11, color: c.textSecondary, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4 }}>
                          {citaForm.cliente_texto && !citaForm.cliente_id && !citaForm.contacto_id ? (
                            <span>{citaForm.cliente_texto} · Texto libre</span>
                          ) : citaForm.cliente_id ? (
                            <>
                              <span>{citaForm.cliente_nombre || "Cliente"} · Cliente</span>
                              {telefonoModalCita() ? (
                                <>
                                  <span>·</span>
                                  <CitaLinkTelefono
                                    telefono={telefonoModalCita()}
                                    onLlamar={llamarDesdeCita}
                                    color={c.accentBlue}
                                  />
                                </>
                              ) : null}
                            </>
                          ) : (
                            <>
                              <span>
                                {[citaForm.contacto_nombre, citaForm.contacto_apellidos].filter(Boolean).join(" ") || "Contacto"}
                                {" · "}
                                {citaForm.contacto_tipo === "propietario" ? "Propietario" : "Colaborador / contacto"}
                              </span>
                              {telefonoModalCita() ? (
                                <>
                                  <span>·</span>
                                  <CitaLinkTelefono
                                    telefono={telefonoModalCita()}
                                    onLlamar={llamarDesdeCita}
                                    color={c.accentBlue}
                                  />
                                </>
                              ) : null}
                            </>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setCitaForm((p) => ({
                              ...p,
                              cliente_id: null,
                              contacto_id: null,
                              cliente_nombre: null,
                              cliente_telefono: null,
                              telefono_contacto: "",
                              contacto_nombre: null,
                              contacto_apellidos: null,
                              contacto_tipo: null,
                              contacto_telefono: null,
                              cliente_texto: null,
                            }))}
                          style={{ fontSize: 10, color: "#b91c1c", background: "none", border: "none", cursor: "pointer", flexShrink: 0 }}
                        >
                          Quitar
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 3 }}>
                      <label style={{ fontSize: 11, color: c.textSecondary, fontWeight: 600, margin: 0 }}>Teléfono contacto</label>
                      {telefonoModalCita() ? (
                        <CitaLinkTelefono
                          telefono={telefonoModalCita()}
                          onLlamar={llamarDesdeCita}
                          color={c.accentBlue}
                          style={{ fontSize: 11 }}
                        />
                      ) : null}
                    </div>
                    <input
                      type="tel"
                      value={citaForm.telefono_contacto ?? telefonoContactoCita(citaForm) ?? ""}
                      onChange={(e) => setCitaForm((p) => ({ ...p, telefono_contacto: e.target.value }))}
                      placeholder="Teléfono del cliente o contacto"
                      disabled={!puedeModificarCampoContacto}
                      title={!puedeModificarCampoContacto ? "No tienes permiso para modificar el contacto" : undefined}
                      style={{ ...inp, opacity: puedeModificarCampoContacto ? 1 : 0.6, cursor: puedeModificarCampoContacto ? "text" : "not-allowed" }}
                    />
                  </div>
                  </>
                  )}
                  {puedeVerCampoPropiedades && (
                  <div style={{ position: "relative" }}>
                    <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 3, fontWeight: 600 }}>Propiedades <span style={{ fontWeight: 500, opacity: 0.75 }}>(opcional)</span></label>
                    <input
                      value={citaPropBuscar}
                      onChange={(e) => setCitaPropBuscar(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        const txt = String(citaPropBuscar || "").trim();
                        if (!txt || !puedeModificarCampoPropiedades) return;
                        setCitaForm((p) => {
                          const list = Array.isArray(p.propiedades_texto) ? [...p.propiedades_texto] : [];
                          if (list.some((x) => String(x).toLowerCase() === txt.toLowerCase())) return p;
                          list.push(txt);
                          return { ...p, propiedades_texto: list };
                        });
                        setCitaPropBuscar("");
                        setCitaPropHits([]);
                      }}
                      placeholder="Ref. CRM o texto libre + Enter"
                      disabled={!puedeModificarCampoPropiedades}
                      title={!puedeModificarCampoPropiedades ? "No tienes permiso para modificar las propiedades" : undefined}
                      style={{ ...inp, opacity: puedeModificarCampoPropiedades ? 1 : 0.6, cursor: puedeModificarCampoPropiedades ? "text" : "not-allowed" }}
                      autoComplete="off"
                    />
                    {citaPropHits.length > 0 && (
                      <div style={{ position: "absolute", zIndex: 30, left: 0, right: 0, top: "100%", marginTop: 2, maxHeight: 200, overflowY: "auto", border: `1px solid ${c.sectionBorder}`, borderRadius: 8, background: c.bgPanel, boxShadow: "0 8px 24px rgba(0,0,0,0.18)" }}>
                        {citaPropHits.map((prop) => {
                          const ya = (citaForm.propiedad_ids || []).includes(prop.id);
                          return (
                            <button
                              key={prop.id}
                              type="button"
                              disabled={ya}
                              onClick={() => {
                                setCitaForm((p) => {
                                  const ids = [...(p.propiedad_ids || [])];
                                  if (ids.includes(prop.id)) return p;
                                  ids.push(prop.id);
                                  return { ...p, propiedad_ids: ids };
                                });
                                setCitaPropMeta((m) => ({ ...m, [prop.id]: { referencia: prop.referencia, titulo: prop.titulo } }));
                                setCitaPropBuscar("");
                                setCitaPropHits([]);
                              }}
                              style={{
                                display: "block",
                                width: "100%",
                                textAlign: "left",
                                padding: "8px 10px",
                                border: "none",
                                borderBottom: `1px solid ${c.sectionBorder}`,
                                background: ya ? (isDark ? "#2a2a30" : "#f0f0ea") : "transparent",
                                cursor: ya ? "default" : "pointer",
                                color: c.textPrimary,
                                opacity: ya ? 0.55 : 1,
                              }}
                            >
                              <div style={{ fontSize: 12, fontWeight: 600 }}>{prop.referencia}{prop.titulo ? ` — ${prop.titulo}` : ""}</div>
                              <div style={{ fontSize: 10, color: c.textMuted }}>{[prop.poblacion, prop.zona].filter(Boolean).join(" · ")}{ya ? " · ya añadida" : ""}</div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {citaPropCargando && <div style={{ fontSize: 10, color: c.textMuted, marginTop: 4 }}>Buscando…</div>}
                    {((citaForm.propiedad_ids || []).length > 0 || (citaForm.propiedades_texto || []).length > 0) && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                        {(citaForm.propiedad_ids || []).map((pid) => (
                          <span
                            key={pid}
                            style={{
                              fontSize: 11,
                              padding: "4px 8px",
                              borderRadius: 6,
                              background: isDark ? "#2a2a34" : "#e8e8e0",
                              border: `1px solid ${c.sectionBorder}`,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              color: c.textPrimary,
                            }}
                          >
                            <CitaLinkReferencia
                              referencia={citaPropMeta[pid]?.referencia || String(pid).slice(0, 8)}
                              onAbrir={() => setFichaResumenPropiedadId(pid)}
                              color={c.accentBlue}
                            />
                            {puedeModificarCampoPropiedades && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCitaForm((p) => ({
                                  ...p,
                                  propiedad_ids: (p.propiedad_ids || []).filter((x) => x !== pid),
                                }));
                              }}
                              style={{ background: "none", border: "none", color: c.textSecondary, cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 0 }}
                              aria-label="Quitar propiedad"
                            >
                              ×
                            </button>
                            )}
                          </span>
                        ))}
                        {(citaForm.propiedades_texto || []).map((txt, idx) => (
                          <span
                            key={`txt-${idx}-${txt}`}
                            style={{
                              fontSize: 11,
                              padding: "4px 8px",
                              borderRadius: 6,
                              background: isDark ? "#2a2a34" : "#e8e8e0",
                              border: `1px solid ${c.sectionBorder}`,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              color: c.textPrimary,
                            }}
                          >
                            <span>{txt} · texto</span>
                            {puedeModificarCampoPropiedades && (
                              <button
                                type="button"
                                onClick={() =>
                                  setCitaForm((p) => ({
                                    ...p,
                                    propiedades_texto: (p.propiedades_texto || []).filter((_, i) => i !== idx),
                                  }))
                                }
                                style={{ background: "none", border: "none", color: c.textSecondary, cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 0 }}
                                aria-label="Quitar texto"
                              >
                                ×
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  )}
                  {puedeVerCampoDireccion && (
                  <div style={{ position: "relative" }}>
                    <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 3, fontWeight: 600 }}>Dirección</label>
                    <AddressAutocompleteInput
                      value={citaForm.direccion || ""}
                      onValueChange={(v) => setCitaForm((p) => ({ ...p, direccion: v }))}
                      placeholder="Buscar dirección… (autocompletar)"
                      disabled={!puedeModificarCampoDireccion}
                      title={!puedeModificarCampoDireccion ? "No tienes permiso para modificar la dirección" : undefined}
                      style={{ ...inp, opacity: puedeModificarCampoDireccion ? 1 : 0.6 }}
                    />
                  </div>
                  )}
                  {puedeVerCampoNotas && (
                  <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                    <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 3, fontWeight: 600 }}>Notas</label>
                    <textarea
                      value={citaForm.notas || ""}
                      onChange={e => setCitaForm(p => ({ ...p, notas: e.target.value }))}
                      rows={2}
                      disabled={!puedeModificarCampoNotas}
                      title={!puedeModificarCampoNotas ? "No tienes permiso para modificar las notas" : undefined}
                      style={{ ...inp, resize: "none", minHeight: 52, flex: 1, opacity: puedeModificarCampoNotas ? 1 : 0.6, cursor: puedeModificarCampoNotas ? "text" : "not-allowed" }}
                    />
                  </div>
                  )}
                </div>
                {/* Derecha: fecha / hora */}
                {puedeVerCampoFechaHora && (
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8, borderLeft: `1px solid ${c.sectionBorder}`, paddingLeft: 22, opacity: puedeModificarCampoFechaHora ? 1 : 0.6, pointerEvents: puedeModificarCampoFechaHora ? "auto" : "none" }} title={!puedeModificarCampoFechaHora ? "No tienes permiso para modificar la fecha/hora" : undefined}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: c.textSecondary, letterSpacing: "0.06em" }}>FECHA Y HORA</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" onClick={() => { citaCalAwaitFinRef.current = false; setCitaTcTarget("inicio"); }} style={{ flex: 1, padding: "6px 8px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, border: `2px solid ${citaTcTarget === "inicio" ? "#0369a1" : c.sectionBorder}`, background: citaTcTarget === "inicio" ? (isDark ? "#0369a133" : "#e0f2fe") : c.bgInput, color: c.textPrimary }}>Inicio</button>
                    <button type="button" onClick={() => { citaCalAwaitFinRef.current = false; setCitaTcTarget("fin"); }} style={{ flex: 1, padding: "6px 8px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, border: `2px solid ${citaTcTarget === "fin" ? "#0369a1" : c.sectionBorder}`, background: citaTcTarget === "fin" ? (isDark ? "#0369a133" : "#e0f2fe") : c.bgInput, color: c.textPrimary }}>Fin</button>
                  </div>
                  <div style={{ fontSize: 11, color: c.textMuted, lineHeight: 1.35 }}>
                    <span style={{ color: c.textSecondary }}>Inicio:</span> {formatCitaPreview(citaForm.fecha_inicio)}
                    <span style={{ margin: "0 6px", color: c.textMuted }}>·</span>
                    <span style={{ color: c.textSecondary }}>Duración:</span> {citaForm.duracion_minutos || minutosDuracionCita(citaForm) || 60} min
                    <span style={{ margin: "0 6px", color: c.textMuted }}>·</span>
                    <span style={{ color: c.textSecondary }}>Fin:</span> {formatCitaPreview(citaForm.fecha_fin)}
                  </div>
                  <div style={{ fontSize: 10, color: c.textMuted, lineHeight: 1.4 }}>
                    Con pestaña Inicio: primer clic en un día fija inicio; el siguiente clic fija fin y activa Fin. Pulsa Inicio otra vez para reiniciar la secuencia. En el calendario, naranja = día de inicio, azul = día de fin. Los selectores y atajos aplican al bloque activo (Inicio o Fin).
                  </div>
                  <div style={{ border: `1px solid ${c.sectionBorder}`, borderRadius: 10, overflow: "hidden", flexShrink: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", background: isDark ? c.bgInput : "#f9f6ef", borderBottom: `1px solid ${c.sectionBorder}` }}>
                      <button type="button" onClick={() => { if (citaCalMonth === 0) { setCitaCalYear((y) => y - 1); setCitaCalMonth(11); } else setCitaCalMonth((m) => m - 1); }} style={{ background: "none", border: "none", color: c.textPrimary, fontSize: 15, cursor: "pointer", lineHeight: 1, padding: "0 6px" }}>‹</button>
                      <span style={{ fontSize: 12, fontWeight: 700, color: c.textPrimary }}>{MESES[citaCalMonth]} {citaCalYear}</span>
                      <button type="button" onClick={() => { if (citaCalMonth === 11) { setCitaCalYear((y) => y + 1); setCitaCalMonth(0); } else setCitaCalMonth((m) => m + 1); }} style={{ background: "none", border: "none", color: c.textPrimary, fontSize: 15, cursor: "pointer", lineHeight: 1, padding: "0 6px" }}>›</button>
                    </div>
                    <div style={{ padding: "0 4px 4px", background: isDark ? c.bgCard : "#ffffff" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", background: isDark ? c.bgPanel : "#f4f1e8", padding: "2px 4px 0" }}>
                          {["L", "M", "X", "J", "V", "S", "D"].map((d, di) => (
                            <div key={d} style={{ textAlign: "center", fontSize: 9, fontWeight: 700, color: di === 6 ? "#b91c1c" : c.textMuted, padding: "2px 0" }}>{d}</div>
                          ))}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", padding: "0 2px 4px" }}>
                          {renderCitaCalendarDays()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: c.textSecondary, marginBottom: 4 }}>
                      Hora {citaTcTarget === "inicio" ? "inicio" : "fin"}
                    </div>
                    <CitaSelectorHora
                      value={`${pad(selH)}:${pad(selM)}`}
                      onChange={(v) => {
                        const [h, m] = v.split(":").map((x) => parseInt(x, 10));
                        applyCitaHM(h, m);
                      }}
                      c={c}
                      isDark={isDark}
                      accentColor="#0369a1"
                      accentBg={isDark ? "rgba(3,105,161,0.2)" : "#e0f2fe"}
                      chipText={c.textPrimary}
                      borderColor={c.sectionBorder}
                      align="left"
                      zIndex={10050}
                    />
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: c.textSecondary, marginBottom: 4 }}>Hora rápida</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {CITA_HORAS_RAPIDAS.map((h) => {
                        const fk = citaTcTarget === "inicio" ? "fecha_inicio" : "fecha_fin";
                        const cur = citaForm[fk] || "";
                        let ch = null;
                        if (cur.includes("T")) ch = parseInt(cur.split("T")[1].split(":")[0], 10);
                        const active = ch === h;
                        return (
                          <button key={`r${h}`} type="button" onClick={() => applyCitaQuickHour(h)} style={{ padding: "4px 9px", borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: "pointer", border: `1px solid ${active ? "#0369a1" : c.sectionBorder}`, background: active ? (isDark ? "#0369a144" : "#bae6fd") : c.bgInput, color: c.textPrimary }}>{h}:00</button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: c.textSecondary, marginBottom: 4 }}>Minutos rápidos</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {CITA_MIN_RAPIDOS.map((m) => {
                        const fk = citaTcTarget === "inicio" ? "fecha_inicio" : "fecha_fin";
                        const cur = citaForm[fk] || "";
                        let cm = null;
                        if (cur.includes("T")) cm = parseInt(cur.split("T")[1].split(":")[1], 10);
                        const active = cm === m;
                        const label = m === 0 ? ":00" : `:${pad(m)}`;
                        return (
                          <button key={m} type="button" onClick={() => applyCitaQuickMin(m)} style={{ padding: "4px 10px", borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: "pointer", border: `1px solid ${active ? "#0369a1" : c.sectionBorder}`, background: active ? (isDark ? "#0369a144" : "#bae6fd") : c.bgInput, color: c.textPrimary }}>{label}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: c.textSecondary, marginBottom: 4 }}>Duración</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {CITA_DURACIONES_RAPIDAS.map((mins) => {
                        const active = Number(citaForm.duracion_minutos) === mins;
                        return (
                          <button
                            key={mins}
                            type="button"
                            onClick={() => applyCitaDuracionRapida(mins)}
                            style={{
                              padding: "4px 9px",
                              borderRadius: 7,
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: "pointer",
                              border: `1px solid ${active ? "#0369a1" : c.sectionBorder}`,
                              background: active ? (isDark ? "#0369a144" : "#bae6fd") : c.bgInput,
                              color: c.textPrimary,
                            }}
                          >
                            {mins}m
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                )}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, paddingTop: 12, borderTop: `1px solid ${c.sectionBorder}`, flexShrink: 0 }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {puedeVerCancelarCita && modalCita !== "nueva" && puedeCancelarCitaTipo({ tipo: modalCita?.tipo || citaForm?.tipo }) && (
                    <button type="button" onClick={() => cancelarCita(modalCita.id)} style={{ background: "#F59E0B22", border: "1px solid #F59E0B", color: "#B45309", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Cancelar cita</button>
                  )}
                  {puedeVerEliminarCita && modalCita !== "nueva" && (
                    <button type="button" onClick={() => eliminarCita(modalCita.id)} style={{ background: "#EF444422", border: "1px solid #EF4444", color: "#EF4444", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12 }}>Eliminar</button>
                  )}
                  {puedeVerResultadoVisita && modalCita !== "nueva" && String(modalCita?.tipo || citaForm?.tipo || "").toLowerCase() === "visita" && (
                    <button
                      type="button"
                      onClick={() => setModalFeedbackAgendaId(String(modalCita.id))}
                      style={{ background: "#2563EB22", border: "1px solid #2563EB", color: "#1d4ed8", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                    >
                      Resultado de la visita
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" onClick={() => setModalCita(null)} style={{ background: "none", border: `1px solid ${c.sectionBorder}`, color: c.textSecondary, borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12 }}>Cancelar</button>
                  <button type="button" onClick={guardarCita} style={{ background: "linear-gradient(135deg, #0369a1, #0ea5e9)", color: "#fff", border: "none", borderRadius: 8, padding: "7px 18px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Guardar</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* MODAL NO DISPONIBLE (crear nuevo) */}
      {modalNoDisp && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setModalNoDisp(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: c.bgPanel, borderRadius: 12, padding: 24, width: 440, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 40px #00000066" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ color: c.textPrimary, margin: 0, fontSize: 16 }}>Marcar no disponibilidad</h3>
              <button onClick={() => setModalNoDisp(false)} style={{ background: "none", border: "none", color: c.textSecondary, fontSize: 20, cursor: "pointer" }}>×</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, color: c.textSecondary, display: "block", marginBottom: 4 }}>Agente *</label>
                <select
                  value={noDispForm.agente_id || ""}
                  onChange={e => setNoDispForm(p => ({...p, agente_id: e.target.value}))}
                  disabled={!puedeModificarNoDisp}
                  title={!puedeModificarNoDisp ? "No tienes permiso para modificar no disponibilidad" : undefined}
                  style={{ ...inputStyle, opacity: puedeModificarNoDisp ? 1 : 0.6, cursor: puedeModificarNoDisp ? "pointer" : "not-allowed" }}
                >
                  <option value="">Seleccionar...</option>
                  {agentes.filter(a => !a.es_ia).map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: c.textSecondary, display: "block", marginBottom: 4 }}>Tipo *</label>
                <select
                  value={noDispForm.tipo || "vacaciones"}
                  onChange={e => setNoDispForm(p => ({...p, tipo: e.target.value}))}
                  disabled={!puedeModificarNoDisp || !puedeModificarTipoNd(noDispForm.tipo || "vacaciones")}
                  title={!puedeModificarNoDisp ? "No tienes permiso para modificar no disponibilidad" : undefined}
                  style={{ ...inputStyle, opacity: puedeModificarNoDisp ? 1 : 0.6, cursor: puedeModificarNoDisp ? "pointer" : "not-allowed" }}
                >
                  {TIPOS_NO_DISP.filter(puedeVerTipoNd).map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: c.textSecondary, display: "block", marginBottom: 4 }}>Motivo *</label>
                <input
                  value={noDispForm.motivo || ""}
                  onChange={e => setNoDispForm(p => ({...p, motivo: e.target.value}))}
                  placeholder="Ej: Médico, Vacaciones..."
                  disabled={!puedeModificarNoDisp}
                  title={!puedeModificarNoDisp ? "No tienes permiso para modificar no disponibilidad" : undefined}
                  style={{ ...inputStyle, opacity: puedeModificarNoDisp ? 1 : 0.6, cursor: puedeModificarNoDisp ? "text" : "not-allowed" }}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="checkbox" id="todoElDia" checked={ndTodoElDia(noDispForm.todo_el_dia)} onChange={e => setNoDispForm(p => ({...p, todo_el_dia: e.target.checked}))} disabled={!puedeModificarNoDisp} style={{ width: 16, height: 16, cursor: puedeModificarNoDisp ? "pointer" : "not-allowed" }} />
                <label htmlFor="todoElDia" style={{ fontSize: 13, color: c.textPrimary, cursor: "pointer" }}>Todo el día</label>
              </div>
              <NoDispFechaHoraPicker
                dtInicio={noDispForm.dt_inicio}
                dtFin={noDispForm.dt_fin}
                todoElDia={ndTodoElDia(noDispForm.todo_el_dia)}
                onChange={(updates) => setNoDispForm((p) => ({ ...p, ...updates }))}
                c={c}
                isDark={isDark}
                esFestivo={esFestivo}
                readOnly={!puedeModificarNoDisp}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
              <button onClick={() => setModalNoDisp(false)} style={{ background: "none", border: `1px solid ${c.sectionBorder}`, color: c.textSecondary, borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13 }}>Cancelar</button>
              <button
                onClick={guardarNoDisp}
                disabled={!puedeModificarNoDisp}
                title={!puedeModificarNoDisp ? "No tienes permiso para guardar no disponibilidad" : undefined}
                style={{ background: "linear-gradient(135deg, #dc2626, #ef4444)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", cursor: puedeModificarNoDisp ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 600, opacity: puedeModificarNoDisp ? 1 : 0.6 }}
              >
                Guardar
              </button>
            </div>
            {!esRolAgente && noDisponible.length > 0 && (
              <div style={{ marginTop: 20, borderTop: `1px solid ${c.sectionBorder}`, paddingTop: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, marginBottom: 8 }}>Períodos registrados</div>
                {noDisponible.map(nd => (
                  <div key={nd.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${c.sectionBorder}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flex: 1 }} onClick={() => { setModalNoDisp(false); abrirEditNd(nd); }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: getColorAgente(nd.agente_id), flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 12, color: c.textPrimary }}>{nd.agente_nombre} — {nd.motivo}</div>
                        <div style={{ fontSize: 11, color: c.textSecondary }}>
                          {formatNdPeriodo(nd)}
                        </div>
                      </div>
                    </div>
                    {puedeModificarNoDisp && (
                      <button onClick={() => eliminarNoDisp(nd.id)} style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: 16 }}>×</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL EDITAR NO DISPONIBILIDAD */}
      {modalEditNd && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setModalEditNd(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: c.bgPanel, borderRadius: 12, padding: 24, width: 440, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 40px #00000066" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ color: c.textPrimary, margin: 0, fontSize: 16 }}>Editar no disponibilidad</h3>
              <button onClick={() => setModalEditNd(null)} style={{ background: "none", border: "none", color: c.textSecondary, fontSize: 20, cursor: "pointer" }}>×</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, color: c.textSecondary, display: "block", marginBottom: 4 }}>Agente *</label>
                <select
                  value={editNdForm.agente_id || ""}
                  onChange={e => setEditNdForm(p => ({...p, agente_id: e.target.value}))}
                  disabled={!puedeModificarNoDisp}
                  title={!puedeModificarNoDisp ? "No tienes permiso para modificar no disponibilidad" : undefined}
                  style={{ ...inputStyle, opacity: puedeModificarNoDisp ? 1 : 0.6, cursor: puedeModificarNoDisp ? "pointer" : "not-allowed" }}
                >
                  <option value="">Seleccionar...</option>
                  {agentes.filter(a => !a.es_ia).map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: c.textSecondary, display: "block", marginBottom: 4 }}>Tipo *</label>
                <select
                  value={editNdForm.tipo || "vacaciones"}
                  onChange={e => setEditNdForm(p => ({...p, tipo: e.target.value}))}
                  disabled={!puedeModificarNoDisp || !puedeModificarTipoNd(editNdForm.tipo || "vacaciones")}
                  title={!puedeModificarNoDisp ? "No tienes permiso para modificar no disponibilidad" : undefined}
                  style={{ ...inputStyle, opacity: puedeModificarNoDisp ? 1 : 0.6, cursor: puedeModificarNoDisp ? "pointer" : "not-allowed" }}
                >
                  {TIPOS_NO_DISP.filter(puedeVerTipoNd).map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: c.textSecondary, display: "block", marginBottom: 4 }}>Motivo *</label>
                <input
                  value={editNdForm.motivo || ""}
                  onChange={e => setEditNdForm(p => ({...p, motivo: e.target.value}))}
                  disabled={!puedeModificarNoDisp}
                  title={!puedeModificarNoDisp ? "No tienes permiso para modificar no disponibilidad" : undefined}
                  style={{ ...inputStyle, opacity: puedeModificarNoDisp ? 1 : 0.6, cursor: puedeModificarNoDisp ? "text" : "not-allowed" }}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="checkbox" id="editTodoElDia"
                  checked={ndTodoElDia(editNdForm.todo_el_dia)}
                  onChange={e => setEditNdForm(p => ({...p, todo_el_dia: e.target.checked}))}
                  disabled={!puedeModificarNoDisp}
                  style={{ width: 16, height: 16, cursor: puedeModificarNoDisp ? "pointer" : "not-allowed" }} />
                <label htmlFor="editTodoElDia" style={{ fontSize: 13, color: c.textPrimary, cursor: "pointer" }}>Todo el día</label>
              </div>
              <NoDispFechaHoraPicker
                dtInicio={editNdForm.dt_inicio}
                dtFin={editNdForm.dt_fin}
                todoElDia={ndTodoElDia(editNdForm.todo_el_dia)}
                onChange={(updates) => setEditNdForm((p) => ({ ...p, ...updates }))}
                c={c}
                isDark={isDark}
                esFestivo={esFestivo}
                readOnly={!puedeModificarNoDisp}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
              {puedeModificarNoDisp ? (
                <button onClick={() => eliminarNoDisp(editNdForm.id)} style={{ background: "#EF444422", border: "1px solid #EF4444", color: "#EF4444", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13 }}>Eliminar</button>
              ) : <span />}
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setModalEditNd(null)} style={{ background: "none", border: `1px solid ${c.sectionBorder}`, color: c.textSecondary, borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13 }}>Cancelar</button>
                <button
                  onClick={guardarEditNd}
                  disabled={!puedeModificarNoDisp}
                  title={!puedeModificarNoDisp ? "No tienes permiso para guardar no disponibilidad" : undefined}
                  style={{ background: "linear-gradient(135deg, #dc2626, #ef4444)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", cursor: puedeModificarNoDisp ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 600, opacity: puedeModificarNoDisp ? 1 : 0.6 }}
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL FESTIVOS — sin scroll, 2 columnas */}
      {modalFestivos && (() => {
        const ambitoColor = isDark
          ? { nacional: '#60a5fa', autonomia: '#c084fc', local: '#f87171' }
          : { nacional: '#1a3a5c', autonomia: '#7c3aed', local: '#b91c1c' };
        const ambitoBg = isDark
          ? { nacional: '#60a5fa28', autonomia: '#c084fc28', local: '#f8717128' }
          : { nacional: '#1a3a5c18', autonomia: '#7c3aed18', local: '#b91c1c18' };
        const ambitoLabel = { nacional: 'Nacional', autonomia: 'C. Valenciana', local: 'Dénia' };
        const festivosDelYear = festivos.filter(f => f.fecha?.startsWith(String(festivosYear)));
        const rowStyle = (f) => ({ display: "flex", alignItems: "center", padding: "7px 16px", gap: 8, opacity: f.activo ? 1 : 0.4, borderBottom: `1px solid ${isDark ? c.border : '#f0f0ec'}` });

        return (
          <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => { setModalFestivos(false); setEditFestivo(null); }}>
            <div onClick={e => e.stopPropagation()} style={{ background: isDark ? c.bgCard : '#ffffff', borderRadius: 16, width: 940, display: "flex", flexDirection: "column", boxShadow: "0 16px 48px rgba(0,0,0,0.30)" }}>

              {/* Header */}
              <div style={{ padding: "14px 20px", borderBottom: `1px solid ${isDark ? c.border : '#e0e0d8'}`, background: isDark ? c.bgPanel : '#f4f4ee', borderRadius: "16px 16px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary }}>📅 Festivos y días no laborables</span>
                  <span style={{ fontSize: 12, color: c.textMuted }}>España + C. Valenciana (auto) · Dénia local (manual)</span>
                  {/* Año automático — navegar otros años si hace falta */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 8 }}>
                    <button onClick={() => setFestivosYear(y => y - 1)} style={{ background: isDark ? c.bgInput : '#e8e8e0', border: "none", color: c.textPrimary, borderRadius: 6, width: 24, height: 24, cursor: "pointer", fontSize: 14, lineHeight: 1 }}>‹</button>
                    <span style={{ fontSize: 15, fontWeight: 700, color: c.accentBlue, minWidth: 46, textAlign: "center" }}>{festivosYear}</span>
                    <button onClick={() => setFestivosYear(y => y + 1)} style={{ background: isDark ? c.bgInput : '#e8e8e0', border: "none", color: c.textPrimary, borderRadius: 6, width: 24, height: 24, cursor: "pointer", fontSize: 14, lineHeight: 1 }}>›</button>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {puedeAltaFestivos && (
                    <button onClick={() => { setEditFestivo({}); setFestivoForm({ fecha: `${festivosYear}-01-01`, nombre: '', ambito: 'local' }); }} style={{ background: "linear-gradient(135deg, #b91c1c, #ef4444)", color: "#fff", border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                      + Añadir festivo local
                    </button>
                  )}
                  <button onClick={() => { setModalFestivos(false); setEditFestivo(null); }} style={{ background: "none", border: "none", color: c.textMuted, fontSize: 22, cursor: "pointer" }}>✕</button>
                </div>
              </div>

              {/* Formulario edición/creación (inline, solo cuando está abierto) */}
              {editFestivo !== null && (() => {
                const esNuevoFestivo = !editFestivo?.id;
                const puedeGuardarFestivo = esNuevoFestivo ? puedeAltaFestivos : puedeModificarFestivos;
                return (
                <div style={{ padding: "10px 20px", borderBottom: `1px solid ${isDark ? c.border : '#e8e8e0'}`, background: isDark ? 'rgba(185,28,28,0.10)' : '#fff5f5', display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                  <div style={{ flex: "0 0 120px" }}>
                    <label style={{ fontSize: 10, color: '#b91c1c', display: "block", marginBottom: 2, fontWeight: 700 }}>FECHA</label>
                    <input
                      type="date"
                      value={festivoForm.fecha || ''}
                      onChange={e => setFestivoForm(p => ({...p, fecha: e.target.value}))}
                      disabled={!puedeModificarFestFecha}
                      style={{ width: "100%", background: isDark ? c.bgInput : '#fff', border: "1px solid #fca5a5", borderRadius: 7, padding: "5px 8px", color: c.textPrimary, fontSize: 12, boxSizing: "border-box", opacity: puedeModificarFestFecha ? 1 : 0.6 }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <label style={{ fontSize: 10, color: '#b91c1c', display: "block", marginBottom: 2, fontWeight: 700 }}>NOMBRE</label>
                    <input
                      value={festivoForm.nombre || ''}
                      onChange={e => setFestivoForm(p => ({...p, nombre: e.target.value}))}
                      placeholder="Ej: Festivo local Dénia"
                      disabled={!puedeModificarFestNombre}
                      style={{ width: "100%", background: isDark ? c.bgInput : '#fff', border: "1px solid #fca5a5", borderRadius: 7, padding: "5px 8px", color: c.textPrimary, fontSize: 12, boxSizing: "border-box", opacity: puedeModificarFestNombre ? 1 : 0.6 }}
                    />
                  </div>
                  <div style={{ flex: "0 0 130px" }}>
                    <label style={{ fontSize: 10, color: '#b91c1c', display: "block", marginBottom: 2, fontWeight: 700 }}>ÁMBITO</label>
                    <select
                      value={festivoForm.ambito || 'local'}
                      onChange={e => setFestivoForm(p => ({...p, ambito: e.target.value}))}
                      disabled={!puedeModificarFestAmbito}
                      style={{ width: "100%", background: isDark ? c.bgInput : '#fff', border: "1px solid #fca5a5", borderRadius: 7, padding: "5px 8px", color: c.textPrimary, fontSize: 12, opacity: puedeModificarFestAmbito ? 1 : 0.6 }}
                    >
                      <option value="nacional">Nacional</option>
                      <option value="autonomia">C. Valenciana</option>
                      <option value="local">Dénia (local)</option>
                    </select>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => { setEditFestivo(null); setFestivoForm({ fecha: '', nombre: '', ambito: 'local' }); }} style={{ background: "none", border: `1px solid ${isDark ? c.border : '#c8c8be'}`, color: c.textMuted, borderRadius: 7, padding: "5px 10px", cursor: "pointer", fontSize: 12 }}>Cancelar</button>
                    <button
                      onClick={guardarFestivo}
                      disabled={savingFestivo || !puedeGuardarFestivo}
                      title={!puedeGuardarFestivo ? "No tienes permiso para guardar festivos" : undefined}
                      style={{ background: "#b91c1c", color: "#fff", border: "none", borderRadius: 7, padding: "5px 14px", cursor: puedeGuardarFestivo ? "pointer" : "not-allowed", fontSize: 12, fontWeight: 600, opacity: puedeGuardarFestivo ? 1 : 0.6 }}
                    >
                      {savingFestivo ? '…' : (editFestivo?.id ? 'Actualizar' : 'Guardar')}
                    </button>
                  </div>
                </div>
                );
              })()}

              {/* Lista agrupada por mes — Ene–Jun izquierda, Jul–Dic derecha */}
              {festivosDelYear.length === 0 ? (
                <div style={{ padding: "40px 20px", textAlign: "center", color: c.textMuted }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📅</div>
                  <div style={{ fontSize: 14 }}>Sin festivos para {festivosYear} — se sincronizarán automáticamente el 1 de enero</div>
                </div>
              ) : (() => {
                // Dedup por fecha+ambito en cliente también (defensa extra)
                const deduped = Object.values(
                  festivosDelYear.reduce((acc, f) => {
                    const k = `${f.fecha}__${f.ambito}`;
                    if (!acc[k] || f.id < acc[k].id) acc[k] = f;
                    return acc;
                  }, {})
                ).sort((a, b) => a.fecha > b.fecha ? 1 : -1);

                // Agrupar por mes (1–12)
                const byMonth = {};
                for (let m = 1; m <= 12; m++) byMonth[m] = [];
                for (const f of deduped) byMonth[parseInt(f.fecha.slice(5, 7))].push(f);

                const renderMes = (mes) => {
                  const items = byMonth[mes];
                  if (items.length === 0) return null;
                  return (
                    <div key={mes}>
                      <div style={{ padding: "7px 16px 4px", fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: c.textMuted, borderBottom: `2px solid ${isDark ? c.border : '#e8e8e0'}`, background: isDark ? c.bgPanel : '#f4f4f0' }}>
                        {MESES[mes - 1].toUpperCase()}
                      </div>
                      {items.map(f => (
                        <div key={f.id} style={{ display: "flex", alignItems: "center", padding: "6px 16px", gap: 8, opacity: f.activo ? 1 : 0.4, borderBottom: `1px solid ${isDark ? c.border : '#f0f0ec'}` }}>
                          <div style={{ width: 44, fontSize: 12, fontWeight: 700, color: f.activo ? '#16a34a' : c.textMuted, flexShrink: 0 }}>
                            {f.fecha?.slice(8)}/{f.fecha?.slice(5, 7)}
                          </div>
                          <div style={{ flex: 1, fontSize: 12, color: c.textPrimary, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.nombre}>
                            {f.nombre}
                          </div>
                          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', background: ambitoBg[f.ambito], color: ambitoColor[f.ambito], borderRadius: 4, padding: "2px 6px", flexShrink: 0, whiteSpace: 'nowrap' }}>
                            {ambitoLabel[f.ambito] || f.ambito}
                          </span>
                          {puedeVerFestActivar && (
                            <button
                              title={f.activo ? "Desactivar" : "Activar"}
                              onClick={() => toggleFestivo(f)}
                              disabled={!puedeModificarFestActivar}
                              style={{ background: "none", border: `1px solid ${isDark ? c.border : '#e0e0d8'}`, borderRadius: 5, width: 24, height: 24, cursor: puedeModificarFestActivar ? "pointer" : "not-allowed", fontSize: 12, color: f.activo ? '#16a34a' : c.textMuted, flexShrink: 0, opacity: puedeModificarFestActivar ? 1 : 0.5 }}
                            >
                              {f.activo ? '✓' : '○'}
                            </button>
                          )}
                          {puedeModificarFestivos && (
                            <button title="Editar" onClick={() => { setEditFestivo(f); setFestivoForm({ fecha: f.fecha, nombre: f.nombre, ambito: f.ambito }); }} style={{ background: "none", border: `1px solid ${isDark ? c.border : '#e0e0d8'}`, borderRadius: 5, width: 24, height: 24, cursor: "pointer", fontSize: 11, color: c.textMuted, flexShrink: 0 }}>✏️</button>
                          )}
                          {puedeBajaFestivos && (
                            <button title="Eliminar" onClick={() => eliminarFestivo(f.id)} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 15, flexShrink: 0, lineHeight: 1 }}>×</button>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                };

                return (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderTop: `1px solid ${isDark ? c.border : '#e8e8e0'}` }}>
                    {/* Enero–Junio */}
                    <div style={{ borderRight: `1px solid ${isDark ? c.border : '#e8e8e0'}` }}>
                      {[1, 2, 3, 4, 5, 6].map(renderMes)}
                    </div>
                    {/* Julio–Diciembre */}
                    <div>
                      {[7, 8, 9, 10, 11, 12].map(renderMes)}
                    </div>
                  </div>
                );
              })()}

              {/* Footer sutil */}
              <div style={{ padding: "8px 20px", borderTop: `1px solid ${isDark ? c.border : '#e8e8e0'}`, background: isDark ? c.bgPanel : '#f9f9f7', borderRadius: "0 0 16px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11, color: c.textMuted }}>
                  Nacional + C. Valenciana se sincronizan automáticamente el 1 de enero y 1 de febrero · Los 2 festivos locales de Dénia se añaden manualmente
                </span>
                <button onClick={() => { setModalFestivos(false); setEditFestivo(null); }} style={{ background: "none", border: `1px solid ${isDark ? c.border : '#c8c8be'}`, color: c.textMuted, borderRadius: 8, padding: "5px 14px", cursor: "pointer", fontSize: 12 }}>Cerrar</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* MODAL VACACIONES — 2 columnas, sin scroll */}
      {modalVacaciones && (() => {
        const inStyle = { width: "100%", background: isDark ? c.bgInput : '#ffffff', border: `1px solid ${isDark ? c.border : '#d0d0c8'}`, borderRadius: 7, padding: "6px 10px", color: c.textPrimary, fontSize: 12, boxSizing: "border-box" };
        const vacExistentes = noDisponible.filter(nd => nd.tipo === 'vacaciones' || nd.tipo === 'baja');
        const { fecha_inicio, fecha_fin } = vacForm;
        const selDias = fecha_inicio && fecha_fin ? Math.round((new Date(fecha_fin) - new Date(fecha_inicio)) / 86400000) + 1 : null;
        const selStatus = !fecha_inicio ? 'Clic en día de inicio…'
          : !fecha_fin ? 'Ahora clic en el día de fin…'
          : `✓ ${fecha_inicio.slice(8)}/${fecha_inicio.slice(5,7)} → ${fecha_fin.slice(8)}/${fecha_fin.slice(5,7)} · ${selDias}d`;
        const calPrev = () => { if (calMonth === 0) { setCalYear(y => y-1); setCalMonth(11); } else setCalMonth(m => m-1); };
        const calNext = () => { if (calMonth === 11) { setCalYear(y => y+1); setCalMonth(0); } else setCalMonth(m => m+1); };
        const agentesReales = agentes.filter(a => !a.es_ia);

        return (
          <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => { setModalVacaciones(false); descartarEdicionVacaciones(); }}>
            <div onClick={e => e.stopPropagation()} style={{ background: isDark ? c.bgCard : '#ffffff', borderRadius: 16, width: 820, display: "flex", flexDirection: "column", boxShadow: "0 16px 48px rgba(0,0,0,0.30)" }}>

              {/* Header */}
              <div style={{ padding: "14px 20px", borderBottom: `1px solid ${isDark ? c.border : '#e0e0d8'}`, background: isDark ? c.bgPanel : '#fffbeb', borderRadius: "16px 16px 0 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <span style={{ fontSize: 16, fontWeight: 700, color: c.textPrimary }}>🏖️ Vacaciones</span>
                  <span style={{ fontSize: 12, color: c.textMuted, marginLeft: 10 }}>
                    {vacEditId ? 'Ajusta fechas o descripción y pulsa Guardar cambios' : 'Selecciona agentes y el período en el calendario'}
                  </span>
                </div>
                <button onClick={() => { setModalVacaciones(false); descartarEdicionVacaciones(); }} style={{ background: "none", border: "none", color: c.textMuted, fontSize: 22, cursor: "pointer" }}>✕</button>
              </div>

              {/* Body: 2 columnas */}
              <div style={{ display: "flex", minHeight: 0 }}>

                {/* ─── COLUMNA IZQUIERDA: formulario ─── */}
                <div style={{ flex: "0 0 360px", padding: "16px 18px", borderRight: `1px solid ${isDark ? c.border : '#e0e0d8'}` }}>
                  {vacEditId && (
                    <div style={{ marginBottom: 12, padding: "8px 10px", borderRadius: 8, background: isDark ? 'rgba(245, 158, 11, 0.15)' : '#fef3c7', border: `1px solid ${isDark ? '#d97706' : '#f59e0b'}`, display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#fcd34d' : '#92400e' }}>Editando un período existente</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        {puedeVerVacEliminar && (
                          <button type="button" onClick={() => eliminarNoDisp(vacEditId)} style={{ background: isDark ? 'rgba(239, 68, 68, 0.25)' : '#fee2e2', border: `1px solid ${isDark ? '#f87171' : '#fecaca'}`, color: isDark ? '#fecaca' : '#b91c1c', cursor: "pointer", fontSize: 11, fontWeight: 700, borderRadius: 6, padding: "4px 10px", whiteSpace: "nowrap" }}>Eliminar período</button>
                        )}
                        <button type="button" onClick={descartarEdicionVacaciones} style={{ background: "none", border: "none", color: isDark ? '#fcd34d' : '#b45309', cursor: "pointer", fontSize: 11, fontWeight: 700, textDecoration: "underline", whiteSpace: "nowrap" }}>Nueva vacación</button>
                      </div>
                    </div>
                  )}
                  {/* Agentes */}
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#b45309', letterSpacing: '0.08em', marginBottom: 7 }}>COMERCIALES</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14, opacity: puedeModificarVacAgentes ? 1 : 0.6, pointerEvents: puedeModificarVacAgentes ? "auto" : "none" }} title={!puedeModificarVacAgentes ? "No tienes permiso para modificar la selección de agentes" : undefined}>
                    {agentesReales.map(a => {
                      const sel = vacAgenteIds.includes(a.id);
                      const aColor = getColorAgente(a.id);
                      return (
                        <label key={a.id} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "5px 10px", borderRadius: 16, border: `2px solid ${sel ? aColor : (isDark ? c.border : '#d0d0c8')}`, background: sel ? aColor + '22' : (isDark ? c.bgInput : '#f9f9f7') }}>
                          <input type="checkbox" checked={sel} onChange={e => setVacAgenteIds(prev => e.target.checked ? [...prev, a.id] : prev.filter(id => id !== a.id))} style={{ display: 'none' }} />
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: aColor, flexShrink: 0 }} />
                          <span style={{ fontSize: 12, color: sel ? (isDark ? '#ffffff' : '#1a1a1a') : c.textPrimary, fontWeight: sel ? 600 : 400 }}>{a.nombre.split(' ')[0]}</span>
                        </label>
                      );
                    })}
                  </div>

                  {/* Calendario */}
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#b45309', letterSpacing: '0.08em', marginBottom: 7 }}>PERÍODO</div>
                  <div style={{ opacity: puedeModificarVacFechas ? 1 : 0.6, pointerEvents: puedeModificarVacFechas ? "auto" : "none" }} title={!puedeModificarVacFechas ? "No tienes permiso para modificar las fechas" : undefined}>
                  <div style={{ border: `1px solid ${isDark ? c.border : '#e0e0d8'}`, borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: isDark ? c.bgPanel : '#f9f6ef', borderBottom: `1px solid ${isDark ? c.border : '#e8e4d8'}` }}>
                      <button onClick={calPrev} style={{ background: "none", border: "none", color: c.textPrimary, fontSize: 16, cursor: "pointer", lineHeight: 1, padding: "0 6px" }}>‹</button>
                      <span style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary }}>{MESES[calMonth]} {calYear}</span>
                      <button onClick={calNext} style={{ background: "none", border: "none", color: c.textPrimary, fontSize: 16, cursor: "pointer", lineHeight: 1, padding: "0 6px" }}>›</button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", background: isDark ? c.bgPanel : '#f4f1e8', padding: "3px 6px 0" }}>
                      {['L','M','X','J','V','S','D'].map((d, i) => (
                        <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: i === 6 ? '#b91c1c' : c.textMuted, padding: "3px 0" }}>{d}</div>
                      ))}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", padding: "0 6px 6px", background: isDark ? c.bgCard : '#ffffff' }}>
                      {renderVacCalendarDays()}
                    </div>
                  </div>

                  {/* Status */}
                  <div style={{ textAlign: "center", fontSize: 12, color: fecha_inicio && fecha_fin ? '#16a34a' : '#b45309', fontWeight: 500, marginBottom: 12 }}>{selStatus}</div>

                  {/* Inputs manuales */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                    <div>
                      <label style={{ fontSize: 10, color: c.textMuted, display: "block", marginBottom: 2 }}>Desde</label>
                      <input type="date" value={fecha_inicio || ''} onChange={e => setVacForm(p => ({...p, fecha_inicio: e.target.value}))} style={inStyle} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: c.textMuted, display: "block", marginBottom: 2 }}>Hasta</label>
                      <input type="date" value={fecha_fin || ''} onChange={e => setVacForm(p => ({...p, fecha_fin: e.target.value}))} style={inStyle} />
                    </div>
                  </div>
                  </div>
                  <div style={{ opacity: puedeModificarVacDescripcion ? 1 : 0.6 }}>
                    <label style={{ fontSize: 10, color: c.textMuted, display: "block", marginBottom: 2 }}>Descripción</label>
                    <input
                      value={vacForm.motivo || ''}
                      onChange={e => setVacForm(p => ({...p, motivo: e.target.value}))}
                      placeholder="Vacaciones"
                      disabled={!puedeModificarVacDescripcion}
                      title={!puedeModificarVacDescripcion ? "No tienes permiso para modificar la descripción" : undefined}
                      style={{ ...inStyle, cursor: puedeModificarVacDescripcion ? "text" : "not-allowed" }}
                    />
                  </div>
                </div>

                {/* ─── COLUMNA DERECHA: vacaciones registradas por agente ─── */}
                <div style={{ flex: 1, padding: "16px 18px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#b45309', letterSpacing: '0.08em', marginBottom: 4 }}>VACACIONES REGISTRADAS</div>
                  <div style={{ fontSize: 10, color: c.textMuted, marginBottom: 10 }}>Clic en las fechas para editar. La <strong style={{ color: isDark ? '#f87171' : '#dc2626' }}>cruz roja</strong> elimina ese período.</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {agentesReales.map(a => {
                      const periodos = vacExistentes.filter(nd => nd.agente_id === a.id).sort((x, y) => x.fecha_inicio > y.fecha_inicio ? 1 : -1);
                      return (
                        <div key={a.id} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px", borderRadius: 8, background: isDark ? c.bgInput : '#fafaf8', border: `1px solid ${isDark ? c.border : '#e8e4d8'}` }}>
                          {/* Nombre agente */}
                          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 90, flexShrink: 0, paddingTop: 2 }}>
                            <div style={{ width: 8, height: 8, borderRadius: "50%", background: getColorAgente(a.id), flexShrink: 0 }} />
                            <span style={{ fontSize: 12, fontWeight: 600, color: c.textPrimary }}>{a.nombre.split(' ')[0]}</span>
                          </div>
                          {/* Períodos en línea */}
                          {periodos.length === 0 ? (
                            <span style={{ fontSize: 11, color: c.textMuted, fontStyle: 'italic', paddingTop: 2 }}>Sin vacaciones</span>
                          ) : (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                              {periodos.map(nd => {
                                const dias = nd.fecha_inicio && nd.fecha_fin ? Math.round((new Date(nd.fecha_fin) - new Date(nd.fecha_inicio)) / 86400000) + 1 : null;
                                const aColor = getColorAgente(nd.agente_id);
                                const editando = vacEditId === nd.id;
                                return (
                                  <div key={nd.id} style={{ display: "inline-flex", alignItems: "center", gap: 2, background: aColor + (isDark ? '33' : '22'), border: editando ? `2px solid #f59e0b` : `1.5px solid ${aColor}88`, borderRadius: 12, padding: "2px 4px 2px 8px", fontSize: 11, boxShadow: editando ? '0 0 0 1px rgba(245,158,11,0.4)' : 'none' }}>
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      title="Clic para editar fechas"
                                      onClick={() => iniciarEdicionVacaciones(nd)}
                                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); iniciarEdicionVacaciones(nd); } }}
                                      style={{ color: isDark ? '#ffffff' : '#1a1a1a', fontWeight: 500, cursor: 'pointer' }}
                                    >
                                      {nd.fecha_inicio?.slice(8)}/{nd.fecha_inicio?.slice(5,7)}→{nd.fecha_fin?.slice(8)}/{nd.fecha_fin?.slice(5,7)}
                                    </span>
                                    {dias && <span style={{ color: isDark ? '#ffffff' : '#555', fontWeight: 700, marginLeft: 2 }}>{dias}d</span>}
                                    {puedeVerVacEliminar && (
                                    <button
                                      type="button"
                                      title="Eliminar este período"
                                      aria-label="Eliminar período"
                                      onClick={(e) => { e.stopPropagation(); eliminarNoDisp(nd.id); }}
                                      style={{
                                        flexShrink: 0,
                                        minWidth: 26,
                                        minHeight: 26,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginLeft: 2,
                                        borderRadius: 6,
                                        border: `1px solid ${isDark ? 'rgba(248,113,113,0.5)' : '#fecaca'}`,
                                        background: isDark ? 'rgba(127,29,29,0.35)' : '#fff',
                                        color: isDark ? '#fecaca' : '#dc2626',
                                        cursor: "pointer",
                                        fontSize: 16,
                                        fontWeight: 700,
                                        lineHeight: 1,
                                        padding: 0,
                                      }}
                                    >×</button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{ padding: "12px 20px", borderTop: `1px solid ${isDark ? c.border : '#e0e0d8'}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: isDark ? c.bgPanel : '#fffbeb', borderRadius: "0 0 16px 16px" }}>
                <button onClick={() => { setModalVacaciones(false); descartarEdicionVacaciones(); }} style={{ background: "none", border: `1px solid ${isDark ? c.border : '#c8c8be'}`, color: c.textMuted, borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 13 }}>Cerrar</button>
                <button onClick={guardarVacaciones} disabled={savingVac || !fecha_inicio || !fecha_fin || vacAgenteIds.length === 0 || !puedeModificarVacaciones}
                  title={!puedeModificarVacaciones ? "No tienes permiso para guardar vacaciones" : undefined}
                  style={{ background: fecha_inicio && fecha_fin && vacAgenteIds.length > 0 && puedeModificarVacaciones ? "linear-gradient(135deg, #d97706, #f59e0b)" : isDark ? '#3a3a4a' : '#d0d0c8', color: fecha_inicio && fecha_fin && vacAgenteIds.length > 0 && puedeModificarVacaciones ? '#fff' : isDark ? '#666' : '#aaa', border: "none", borderRadius: 8, padding: "7px 20px", cursor: fecha_inicio && fecha_fin && vacAgenteIds.length > 0 && puedeModificarVacaciones ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 600 }}>
                  {savingVac ? 'Guardando…' : vacEditId ? 'Guardar cambios' : `Guardar${vacAgenteIds.length > 1 ? ` (${vacAgenteIds.length} agentes)` : ''}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <ModalGuardiasSabados
        isOpen={modalGuardiasSabados}
        onClose={() => setModalGuardiasSabados(false)}
        theme={theme}
        onGuardiasChanged={() => { cargarNoDisponible(); cargarGuardiasDiaSemana(); }}
      />

      <ModalGuardiaDia
        isOpen={modalGuardiaDia}
        onClose={() => setModalGuardiaDia(false)}
        theme={theme}
        onGuardiasChanged={() => { cargarNoDisponible(); cargarGuardiasDiaSemana(); }}
      />

      <ModalFeedbackVisita
        isOpen={Boolean(modalFeedbackAgendaId)}
        onClose={() => setModalFeedbackAgendaId(null)}
        agendaId={modalFeedbackAgendaId}
        theme={theme}
        onCompleted={() => {
          setModalFeedbackAgendaId(null);
          setModalCita(null);
          cerrarModalVisita();
          cargarCitas();
        }}
      />

      {modalVisitaOpen && (
        <ModalCita
          isOpen={true}
          onClose={cerrarModalVisita}
          onConfirm={handleVisitaConfirmAgenda}
          modoEdicion={Boolean(visitaAgendaId)}
          initialValues={visitaInitial}
          initialClienteTexto={visitaClienteTexto}
          contact={contactoModalVisita(visitaSnapshot || {})}
          properties={visitaProperties}
          agentName={visitaSnapshot?.agente_nombre || ""}
          onLlamarTelefono={llamarDesdeVisita}
          onAbrirVistaPrevia={setFichaResumenPropiedadId}
          onAbrirFichaCliente={(clienteId) => { if (clienteId) transferVisitaModalAFicha(`/clientes/${clienteId}`); }}
          onAbrirFichaContacto={(contactoId) => { if (contactoId) transferVisitaModalAFicha(`/contactos/${contactoId}`); }}
          onBuscarPropiedadPorRef={(ref) => fetchPropiedadPorRef(api, ref)}
          footerExtra={visitaAgendaId ? (
            <>
              {puedeVerEliminarCita && (
                <button
                  type="button"
                  onClick={() => eliminarCita(visitaAgendaId)}
                  style={{ background: "#EF444422", border: "1px solid #EF4444", color: "#EF4444", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12 }}
                >
                  Eliminar
                </button>
              )}
              {puedeVerResultadoVisita && (
                <button
                  type="button"
                  onClick={() => {
                    setModalFeedbackAgendaId(String(visitaAgendaId));
                    cerrarModalVisita();
                  }}
                  style={{ background: "linear-gradient(135deg, #0369a1, #0ea5e9)", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                >
                  Resultado de la visita
                </button>
              )}
            </>
          ) : null}
        />
      )}

      {fichaResumenPropiedadId && (
        <FichaResumenPropiedad
          propiedadId={fichaResumenPropiedadId}
          onClose={() => setFichaResumenPropiedadId(null)}
        />
      )}

    </div>
  );
}
